import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { logPrint, logDownload } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import FileDropZone from '../components/FileDropZone';
import { getKstDateString } from '../utils/dateKst';

// 첨부파일 다운로드 - 인증된 axios 인스턴스(API)로 blob을 받아 강제 다운로드한다.
// (예전에는 http://192.168.90.36:3001/... 을 하드코딩한 <a href> 링크를 썼는데, 이 주소가
// 앱의 실제 API 주소(api.js의 baseURL, nine-ims.iptime.org)와 달라서 접속이 안 되고, 게다가
// 순수 <a> 태그 이동 방식이라 로그인 토큰(Authorization 헤더)도 실리지 않아 다운로드가 되지
// 않았다 — 목록/상세 모두 이 함수로 통일한다.)
function downloadCertFile(fileName, oriName, onError) {
  API.get(`/certs/download/${encodeURIComponent(fileName)}`, { responseType: 'blob' })
    .then(res => {
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = oriName || fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      logDownload('인증관리', oriName || fileName);
    })
    .catch(err => {
      console.error('다운로드 실패:', err.message);
      if (onError) onError();
    });
}

// 만료일 D-Day 계산 함수
function calcDday(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

// D-Day 뱃지 색상
// plusFormat: true면 만료일이 지난 경우 "만료 N일 초과" 대신 "D+N" 형태로만 표기한다(지식재산권 리스트 전용).
function DdayBadge({ date, plusFormat }) {
  const d = calcDday(date);
  if (d === null) return null;
  const color = d < 0 ? '#dc2626' : d <= 30 ? '#d97706' : '#16a34a';
  const bg    = d < 0 ? '#fee2e2' : d <= 30 ? '#fef3c7' : '#dcfce7';
  const label = d < 0 ? (plusFormat ? `D+${Math.abs(d)}` : `만료 ${Math.abs(d)}일 초과`) : d === 0 ? '오늘 만료!' : `D-${d}`;
  return <span style={{ fontSize:11, padding:'3px 13px', borderRadius:20, background:bg, color, fontWeight:600 }}>{label}</span>;
}

// 인증항목(카드) 배지/포인트 색상 — 등록된 이름은 자유 텍스트라 미리 다 정할 수 없으므로,
// 이름을 해시해서 팔레트 중 하나를 고정 배정한다(같은 이름은 항상 같은 색) — 계약관리와 동일한 방식.
const TAB_PALETTE = [
  { color:'#185FA5', bg:'#E6F1FB' }, { color:'#0F6E56', bg:'#E1F5EE' },
  { color:'#854F0B', bg:'#FAEEDA' }, { color:'#993556', bg:'#FBEAF0' },
  { color:'#6d28d9', bg:'#EDE4FB' }, { color:'#DC2626', bg:'#FEE2E2' },
  { color:'#0EA5E9', bg:'#E0F2FE' }, { color:'#DB2777', bg:'#FCE7F3' },
];
function tabStyleOf(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return TAB_PALETTE[hash % TAB_PALETTE.length];
}

const navBtnStyle = { background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' };

// 인증항목(카드)별로 목록 표의 컬럼 구성/순서를 다르게 적용하기 위한 설정.
// "전체" 카드 선택시와, 아직 개별적으로 정해지지 않은 인증항목은 DEFAULT_CERT_LIST_COLUMNS를 그대로
// 사용하고, 사용자가 인증항목별로 목록을 하나씩 확정해줄 때마다 CERT_LIST_LAYOUTS에 항목을 추가해나간다.
// 정부인증은 "전체" 선택시와 동일한 구성을 쓰도록 DEFAULT_CERT_LIST_COLUMNS를 그대로 참조한다.
const DEFAULT_CERT_LIST_COLUMNS = [
  { key: 'cert_type', label: '인증항목', width: '180px' },
  { key: 'created_at', label: '등록일', width: '120px' },
  { key: 'org_name', label: '인증기관', width: '220px' },
  { key: 'cert_name', label: '인증명', width: '1fr' },
  { key: 'cert_number', label: '인증번호', width: '220px' },
  { key: 'start_date', label: '취득일', width: '130px' },
  { key: 'expire_date', label: '만료일', width: '130px' },
  { key: 'dday', label: 'D-Day', width: '90px' },
  { key: 'download', label: '다운로드', width: '70px' },
];
// 지식재산권 카드 전용 리스트 컬럼 구성 — 인증항목/등록일/인증기관/출원명/출원번호/출원일/특허등록일/연차료/D-Day/다운로드.
// "연차료"(다음 연차료 납부기한)는 지식재산권만 쓰는 별도 날짜라 전용 필드가 없어, 폼에서 적용범위(scope)를 삭제한 자리를
// 재사용해 저장한다(scope 컬럼 = 지식재산권에 한해 "연차만료일/연차료 납부기한" 날짜 문자열) — CertForm의 scopeAsDate 설정과 짝이 맞다.
// D-Day는 이 scope(연차료 납부기한) 값을 기준으로 계산되며(renderCertListCell 'dday' case), 상세화면의
// "연차료" 버튼으로 납부 처리할 때마다 scope 값이 연장되므로 별도 로직 변경 없이 자동으로 함께 갱신된다.
const IP_LIST_COLUMNS = [
  { key: 'cert_type', label: '인증항목', width: '180px' },
  { key: 'created_at', label: '등록일', width: '110px' },
  { key: 'org_name', label: '인증기관', width: '170px' },
  { key: 'cert_name', label: '출원명', width: '1fr' },
  { key: 'cert_number', label: '출원번호', width: '160px' },
  { key: 'start_date', label: '출원일', width: '110px' },
  { key: 'expire_date', label: '특허등록일', width: '110px' },
  { key: 'scope', label: '연차만료일', width: '110px' },
  { key: 'dday', label: 'D-Day', width: '90px' },
  { key: 'download', label: '다운로드', width: '90px' },
];
// 산업안전인증(KCs) 카드의 "양식관리" 관리항목 전용 — 트리구조 없이 문서번호/문서명 중심의
// 문서서식(일반관리/품질관리/생산관리에서 문서 작성시 재사용할 .hwp 등 양식) 라이브러리 목록.
const FORM_LIB_LIST_COLUMNS = [
  { key: 'cert_number', label: '문서번호', width: '200px' },
  { key: 'cert_name', label: '문서명', width: '1fr' },
  { key: 'created_at', label: '등록일', width: '120px' },
  { key: 'download', label: '다운로드', width: '80px' },
];
const CERT_LIST_LAYOUTS = {
  '정부인증': DEFAULT_CERT_LIST_COLUMNS,
  '지식재산권': IP_LIST_COLUMNS,
};
// 트리구조(menu_type='tree') 카드의 모든 노드(양식관리 포함) 공용 목록 컬럼 구성 — 좌측 트리에서
// 폴더를 고를 때마다 우측에 뜨는 목록은 이제 어느 노드든 이 6개 컬럼으로 통일한다.
// (기존에는 "양식관리" 노드만 FORM_LIB_LIST_COLUMNS라는 별도 4컬럼 구성을 썼으나, 트리구조 카드 전체를
// 통일하기로 하면서 FORM_LIB_LIST_COLUMNS는 더 이상 목록에는 쓰이지 않고 폼 레이아웃(FORM_LIB_FORM_LAYOUT)에만 남는다.)
// "개정" 한 컬럼이던 것을 제정(최초 등록일)/개정(Rev.NN)/개정일(가장 최근 개정 일자) 3개로 분리한다.
const TREE_NODE_LIST_COLUMNS = [
  { key: 'cert_type', label: '인증항목', width: '140px' },
  { key: 'created_at', label: '등록일', width: '110px' },
  { key: 'cert_number', label: '문서번호', width: '150px' },
  { key: 'cert_name', label: '파일명', width: '1fr' },
  { key: 'enact_date', label: '제정', width: '100px' },
  { key: 'revision', label: '개정', width: '80px' },
  { key: 'revision_date', label: '개정일', width: '100px' },
  { key: 'download', label: '다운로드', width: '90px' },
];
// 위 TREE_NODE_LIST_COLUMNS 중 정렬(오름/내림차순) 아이콘을 지원하는 컬럼 — 다운로드는 정렬 대상이 아니다.
const TREE_SORTABLE_KEYS = new Set(['cert_type', 'created_at', 'cert_number', 'cert_name', 'enact_date', 'revision', 'revision_date']);
// 긴 텍스트라 말줄임표(ellipsis) 처리가 필요한 컬럼 — 정렬은 다른 컬럼과 동일하게 항상 가운데 정렬한다.
const CERT_LIST_TRUNCATE_KEYS = new Set(['cert_name', 'org_name']);
// 목록에서 "등록일/취득일/만료일/D-Day/다운로드"(및 각 레이아웃별 동일 성격의 날짜·아이콘 컬럼)는
// 그대로 가운데 정렬을 유지하고, 그 외 나머지 항목(인증항목/인증기관/인증명/인증번호 등)은 왼쪽정렬 +
// 약 한글자 들여쓰기를 적용한다.
const CERT_LIST_CENTER_KEYS = new Set([
  'created_at', 'start_date', 'expire_date', 'dday', 'download', // DEFAULT/IP 공통 5종
  'scope',                                    // 지식재산권 "연차만료일" — 만료일과 동일 성격
  'enact_date', 'revision', 'revision_date',  // 트리구조 "제정/개정/개정일" — 등록일/취득일/만료일과 동일 성격
]);

// extra: { canDownload, onDownloadError } — 목록의 다운로드 아이콘은 다운로드 권한이 있는 계정에게만 클릭 가능하게 표시된다.
function renderCertListCell(key, c, extra) {
  const isIp = c.cert_type === '지식재산권';
  switch (key) {
    case 'cert_type': {
      const style = tabStyleOf(c.cert_type);
      return (
        <span style={{ fontSize:13, padding:'3px 10px', borderRadius:20, fontWeight:600, background:style.bg, color:style.color }}>
          {c.cert_type}
        </span>
      );
    }
    case 'dday':
      // 지식재산권은 "만료일"이 scope 컬럼에 저장되어 있고(폼 참고), 만료 초과시 D+n로만 표기한다.
      return <DdayBadge date={isIp ? c.scope : c.expire_date} plusFormat={isIp} />;
    case 'created_at':
      return c.created_at?.slice(0, 10) || '-';
    case 'enact_date':
      // 제정일 — 문서(레코드)가 최초 등록된 날짜. 이후 개정이 있어도 바뀌지 않는다.
      return c.created_at?.slice(0, 10) || '-';
    case 'revision':
      // 개정차수 — 같은 레코드(문서)의 파일/내용이 교체될 때마다 1씩 증가(routes/certs.js 참고). 신규 등록시 Rev.01.
      return `Rev.${String(c.revision_no || 1).padStart(2, '0')}`;
    case 'revision_date':
      // 개정일 — 가장 최근 개정이 일어난 날짜. 아직 한번도 개정되지 않았으면 제정일(등록일)과 동일하게 표시한다.
      return (c.revised_at || c.created_at)?.slice(0, 10) || '-';
    case 'download': {
      const files = [
        { name: c.file_name, ori: c.file_ori, label: isIp ? '출원번호통지서' : '첨부파일' },
        { name: c.file_name2, ori: c.file_ori2, label: '출원서' },
      ].filter(f => f.name);
      if (files.length === 0) return <span style={{ color:'#ddd' }}>-</span>;
      return (
        <span style={{ display:'inline-flex', gap:6 }}>
          {files.map(f => !extra?.canDownload ? (
            <span key={f.name} title={`${f.label}: ${f.ori || ''}`} style={{ color:'#bbb', fontSize:15 }}>📎</span>
          ) : (
            <span key={f.name}
              onClick={e => { e.stopPropagation(); downloadCertFile(f.name, f.ori, extra?.onDownloadError); }}
              title={`${f.label} 다운로드: ${f.ori || ''}`}
              style={{ fontSize:16, cursor:'pointer' }}>
              ⬇️
            </span>
          ))}
        </span>
      );
    }
    default:
      return c[key] || '-';
  }
}

// 관리항목 트리 유틸 — cert_manage_items의 평평한(flat) 배열(id/parent_id)을 중첩 트리로 변환/평탄화한다.
// 트리구조(menu_type='tree') 카드의 좌측 트리 렌더링과, 등록폼 select의 들여쓰기 표시에 공용으로 쓰인다.
function buildManageTree(flat) {
  const byParent = {};
  (flat || []).forEach(item => {
    const key = item.parent_id || 'root';
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(item);
  });
  const attach = (parentKey) => (byParent[parentKey] || []).map(item => ({ ...item, children: attach(item.id) }));
  return attach('root');
}
function flattenManageTree(tree, depth = 0) {
  let out = [];
  (tree || []).forEach(node => {
    out.push({ id: node.id, label: (depth > 0 ? '　'.repeat(depth) + '└ ' : '') + node.name });
    if (node.children && node.children.length) out = out.concat(flattenManageTree(node.children, depth + 1));
  });
  return out;
}
// 트리구조 카드의 "메인 메뉴"(최상위 관리항목) 탭 구조용 — 중첩 트리에서 id로 노드를 찾거나,
// 어떤 노드 자신 + 그 하위 모든 노드의 id를 한번에 모아준다(메인 메뉴 탭의 "전체" 집계 카운트/필터링에 사용).
function findNodeById(tree, id) {
  for (const node of (tree || [])) {
    if (node.id === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return null;
}
function collectSubtreeIds(node) {
  if (!node) return [];
  let ids = [node.id];
  (node.children || []).forEach(c => { ids = ids.concat(collectSubtreeIds(c)); });
  return ids;
}
// 전체보기(activeCategory === '전체')에서 이 인증(cert) 한 건이 노출 대상인지 여부 — 트리구조 카드
// (산업안전인증(KCs) 등)는 "인증관리" 밑 "인증서" 하위메뉴 등록분만 노출하고, 그 외(양식관리/일반관리/
// 품질관리/생산관리)는 제외한다. 비트리구조 카드(정부인증/지식재산권 등)는 제한 없이 전체 노출.
// 실제 목록 필터(filtered)와 상단 "전체" 카드의 등록건수 집계 양쪽에서 공용으로 써서 두 숫자가 항상 일치하게 한다.
function isVisibleInAllView(c, categories) {
  const cardOfRow = categories.find(cc => cc.name === c.cert_type);
  return cardOfRow?.menu_type !== 'tree' || c.manage_item === '인증서';
}

export default function Certs() {
  const [certs, setCerts] = useState([]);
  const [categories, setCategories] = useState([]);   // 인증항목 등록 - 카드 목록
  const [manageItems, setManageItems] = useState([]); // 관리항목 - 현재 선택된 인증항목(카드)에 종속된 하위 분류 목록
  const [selected, setSelected] = useState(null);    // 상세 보기
  const [showForm, setShowForm] = useState(false);   // 등록/수정 폼
  const [editData, setEditData] = useState(null);    // 수정 시 기존 데이터
  const [showCategoryModal, setShowCategoryModal] = useState(false);   // 인증항목 등록 모달
  const [showManageItemModal, setShowManageItemModal] = useState(false); // 관리항목 등록 모달
  const [msg, setMsg] = useState('');

  // 인증갱신 - 상세 모달 내 인라인 입력폼 상태
  const [showRenewForm, setShowRenewForm] = useState(false);
  const [renewData, setRenewData] = useState({ cert_number:'', org_name:'', start_date:'', expire_date:'', note:'' });
  const [renewFile, setRenewFile] = useState(null);
  const [renewLoading, setRenewLoading] = useState(false);

  // 연차료(지식재산권 전용) - 상세 모달 내 1/2/3년 선택 패널 상태
  const [showAnnuityPanel, setShowAnnuityPanel] = useState(false);
  const [annuityLoading, setAnnuityLoading] = useState(false);
  const [patentRegLoading, setPatentRegLoading] = useState(false);

  // 목록 다중선택 - 이동/삭제 (정부인증/지식재산권을 제외한 나머지 인증항목에서만 사용).
  // 정부인증/지식재산권은 카드별로 이미 전용 UI(발급기관 목록/특허 연차료 등)가 확정돼 있어
  // 관리항목 간 "이동" 개념이 맞지 않으므로 이 기능 대상에서 뺀다.
  const [selectedCertIds, setSelectedCertIds] = useState(new Set());
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // 목록 컬럼명 클릭 정렬 — 항상 오름차순만 사용(내림차순 없음). 같은 컬럼을 다시 누르면
  // 정렬을 해제하고 기본순으로 돌아간다.
  const [sortKey, setSortKey] = useState(null);   // null이면 기본순(등록일 오름차순)
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); }
    else { setSortKey(null); }
  };

  // "+ 인증 등록" 클릭시 - 트리구조 카드에서 특정 관리항목(양식관리 제외)을 선택한 상태면
  // "파일 업로드로 등록" / "양식 기반으로 작성" 중 하나를 먼저 고르게 한다.
  const [showComposerChoice, setShowComposerChoice] = useState(false);
  const [showDocComposer, setShowDocComposer] = useState(false);

  // 목록(트리구조 카드) 드래그앤드롭 업로드 - 파일을 목록 위에 끌어다 놓으면 별도 폼 없이 바로 등록.
  const [isDragOverList, setIsDragOverList] = useState(false);
  const [dropUploading, setDropUploading] = useState(false);

  const { hasDetail, canWrite: canWriteMenu, hasListItem } = usePermissions();
  const canDownload   = hasDetail('certs', 'download_allowed');
  const canDeleteCert = hasDetail('certs', 'delete_allowed');
  const canWriteCerts = canWriteMenu('certs');
  const canManageCategory  = hasDetail('certs', 'category_manage');
  const canManageManageItem = hasDetail('certs', 'manage_item_manage');
  // 선택된 인증(상세보기)에 대해 실제로 수정/삭제 가능한지 — 등록된 인증항목별 쓰기권한(category_write_scope) 검사
  const canEditSelected   = canWriteCerts && !!selected && hasListItem('certs', 'category_write_scope', selected.cert_type);
  const canDeleteSelected = canWriteCerts && !!selected && hasListItem('certs', 'category_write_scope', selected.cert_type);
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  const load = () => API.get('/certs').then(r => setCerts(r.data)).catch(() => {});
  const loadCategories = () => API.get('/certs/categories').then(r => setCategories(r.data)).catch(() => {});
  // 관리항목은 이제 인증항목(카드) 1개에 종속되므로, 선택된 카드의 id로만 조회한다.
  const loadManageItems = (categoryId) => {
    if (!categoryId) { setManageItems([]); return; }
    API.get(`/certs/manage-items?category_id=${categoryId}`).then(r => setManageItems(r.data)).catch(() => {});
  };
  // category_id 없이 전체 관리항목을 한번에 불러와서, 트리구조 카드의 상단 카드 등록건수(=관리항목 등록건수)를
  // 계산하는 데 쓴다(카드마다 매번 따로 조회하지 않도록 한번만 불러와 category_id로 걸러 씀).
  const [allManageItems, setAllManageItems] = useState([]);
  const loadAllManageItems = () => API.get('/certs/manage-items').then(r => setAllManageItems(r.data)).catch(() => {});
  useEffect(() => { load(); loadCategories(); loadAllManageItems(); }, []);

  // 상세 모달에서 다른 인증서를 선택하거나 닫으면 인증갱신/연차료 입력폼 상태 초기화
  useEffect(() => { setShowRenewForm(false); setRenewFile(null); setShowAnnuityPanel(false); }, [selected?.id]);

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 인증관리 항목을 클릭해 들어온 경우, 그 항목의
  // 상세 모달을 바로 연다. certs가 아직 로드되기 전(마운트 직후)일 수 있으므로 로드될 때까지 대기.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const ao = location.state?.activityOpen;
    if (ao && certs.length) {
      const found = certs.find(c => String(c.id) === String(ao.itemId));
      if (found) setSelected(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, certs]); // eslint-disable-line

  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  // ── 상단 카드메뉴(인증항목) ─────────────────────────────────
  const [activeCategory, setActiveCategory] = useState('전체');
  const selectedCategoryObj = categories.find(c => c.name === activeCategory) || null; // 전체 선택시 null
  const menuType = selectedCategoryObj?.menu_type === 'tree' ? 'tree' : 'top'; // 선택된 카드의 하위 관리항목 표시방식
  // 목록 다중선택(체크박스로 이동/삭제) 허용 여부 — 정부인증/지식재산권을 제외한 특정 인증항목(카드)
  // 하나가 선택돼 있을 때만 노출한다("전체보기"에서는 여러 카드가 섞여 있어 이동 대상 관리항목을
  // 하나로 특정할 수 없으므로 제외).
  const canBulkManage = activeCategory !== '전체' && activeCategory !== '정부인증' && activeCategory !== '지식재산권';

  // ── 관리항목(선택된 인증항목 카드에 종속) ────────────────────
  // 상단메뉴(top)면 최상위 항목이 그대로 탭으로 표시된다. 트리구조(tree)면 최상위 항목이 "메인 메뉴" 탭이
  // 되고(activeMainMenuId), 그 메인 메뉴를 선택하면 하위 항목들이 좌측 디렉토리 트리로 표시된다.
  // 전체 카드가 선택되면 표시하지 않는다. 트리구조에서는 같은 이름이 서로 다른 부모 아래 중복 등록될 수
  // 있으므로, 이름이 아닌 id로 선택/필터링한다.
  const [activeManageItemId, setActiveManageItemId] = useState('전체');
  // 트리구조 카드 전용 - 상단 탭으로 표시되는 "메인 메뉴"(최상위 관리항목) 선택 상태.
  // '전체'면 메인 메뉴 구분 없이 카드 전체를 보여주고(좌측 트리 숨김), 특정 메인 메뉴를 선택하면
  // 그 메인 메뉴의 하위 항목들만 좌측 디렉토리 트리로 표시된다.
  const [activeMainMenuId, setActiveMainMenuId] = useState('전체');
  // 트리구조 카드로 전환할 때마다, 그 카드의 관리항목이 로드되는 즉시 딱 한번 첫번째 메인 메뉴를 자동
  // 선택해준다(사용자가 카드 선택 후 또 한번 메인 메뉴 탭을 눌러야 하는 수고를 덜기 위함). 이후 사용자가
  // 관리항목을 추가/삭제해서 manageItems가 다시 갱신되더라도, 이미 이 카드에서 자동선택을 했다면 건드리지 않는다.
  const autoSelectedMainMenuForRef = useRef(null);
  useEffect(() => {
    setActiveManageItemId('전체');
    setActiveMainMenuId('전체');
    autoSelectedMainMenuForRef.current = null;
    loadManageItems(selectedCategoryObj?.id);
  }, [activeCategory, categories]); // eslint-disable-line
  useEffect(() => {
    if (menuType !== 'tree') return;
    if (autoSelectedMainMenuForRef.current === activeCategory) return;
    const roots = manageItems.filter(m => !m.parent_id);
    if (roots.length > 0) {
      setActiveMainMenuId(roots[0].id);
      setActiveManageItemId('전체');
      autoSelectedMainMenuForRef.current = activeCategory;
    }
  }, [manageItems, menuType, activeCategory]); // eslint-disable-line

  // 트리구조 카드에서, 선택된 메인 메뉴(activeMainMenuId)에 해당하는 노드와 그 하위 전체 id 목록
  // (메인 메뉴 탭의 "전체" 집계 카운트/필터링, 좌측 트리에 표시할 하위 트리 계산에 사용).
  const manageTree = buildManageTree(manageItems);
  const activeMainMenuNode = activeMainMenuId !== '전체' ? findNodeById(manageTree, activeMainMenuId) : null;
  const mainMenuSubtreeIds = activeMainMenuNode ? collectSubtreeIds(activeMainMenuNode) : [];
  // 산업안전인증(KCs) 카드의 "양식관리" 메인 메뉴는 하위 폴더(트리) 없이, 향후 일반관리/품질관리/생산관리에서
  // 문서를 작성할 때 재사용할 문서서식(.hwp 등) 라이브러리로 별도 취급한다 — 좌측 디렉토리 트리를 숨기고
  // 문서번호/문서명 중심의 간단한 목록·등록폼을 사용한다(아래 isFormLibNode 사용처 참고).
  const isFormLibNode = menuType === 'tree' && activeMainMenuNode?.name === '양식관리';

  // 트리구조 좌측 패널 - 펼침/접힘 상태(펼쳐진 노드 id의 Set). 메인 메뉴를 새로 선택할 때마다
  // 트리는 모두 접힌 상태로 시작한다(사용자가 ▸를 눌러 필요한 폴더만 펼쳐본다).
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  useEffect(() => {
    setExpandedNodes(new Set());
  }, [activeMainMenuId]);
  const toggleExpandNode = (id) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── 페이지당 표시개수 (계약관리와 동일) ─────────────────────
  const [pageSize, setPageSize] = useState('all');   // 10 / 30 / 50 / 100 / 'all'
  const [page, setPage] = useState(1);

  // ── 검색 ─────────────────────────────────────────────────────
  const [search, setSearch] = useState('');

  let filtered = certs.filter(c => {
    const matchCategory = activeCategory === '전체' || c.cert_type === activeCategory;
    // 트리구조 카드: 메인 메뉴(activeMainMenuId)가 '전체'면 카드 전체(필터 없음), 메인 메뉴는 선택했지만
    // 그 안에서 특정 하위 폴더까지는 안 골랐으면(activeManageItemId==='전체') 메인 메뉴 자신+하위 전체를 집계,
    // 특정 하위 폴더까지 선택했으면 그 폴더만 정확히 일치.
    // 상단메뉴(top) 카드: 기존과 동일하게 항목 id 정확히 일치(또는 '전체'면 필터 없음).
    const matchManageItem = menuType === 'tree'
      ? (activeMainMenuId === '전체'
          ? true
          : (activeManageItemId === '전체' ? mainMenuSubtreeIds.includes(c.manage_item_id) : c.manage_item_id === activeManageItemId))
      : (activeManageItemId === '전체' || c.manage_item_id === activeManageItemId);
    // 전체보기(activeCategory === '전체')에서는 트리구조 카드(산업안전인증(KCs) 등)의 인증관리/양식관리/
    // 일반관리/품질관리/생산관리 하위메뉴가 뒤섞여 노출되지 않도록, 그 중 "인증서" 하위메뉴에 등록된 것만
    // 노출한다(정부인증/지식재산권 등 트리구조가 아닌 카드는 이 제한 없이 그대로 전체 노출).
    const matchAllViewTreeFilter = activeCategory !== '전체' || isVisibleInAllView(c, categories);
    const matchSearch = !search ||
      c.cert_name.includes(search) || c.cert_type.includes(search) || (c.org_name || '').includes(search) || (c.cert_number || '').includes(search);
    return matchCategory && matchManageItem && matchAllViewTreeFilter && matchSearch;
  });
  // 목록 헤더(컬럼명)를 클릭해 오름/내림차순을 지정한 경우 우선 적용 — 트리구조뿐 아니라
  // 정부인증/지식재산권 등 그리드형 목록에서도 동일하게 동작한다(sortable 컬럼 전체 공용 로직).
  if (sortKey) {
    const DATE_SORT_KEYS = new Set(['start_date', 'expire_date', 'scope']);
    filtered = [...filtered].sort((a, b) => {
      let av, bv;
      if (sortKey === 'revision') { av = a.revision_no || 1; bv = b.revision_no || 1; }
      else if (sortKey === 'created_at' || sortKey === 'enact_date') { av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime(); }
      else if (sortKey === 'revision_date') { av = new Date(a.revised_at || a.created_at || 0).getTime(); bv = new Date(b.revised_at || b.created_at || 0).getTime(); }
      else if (DATE_SORT_KEYS.has(sortKey)) { av = new Date(a[sortKey] || 0).getTime(); bv = new Date(b[sortKey] || 0).getTime(); }
      else { av = (a[sortKey] || '').toString(); bv = (b[sortKey] || '').toString(); }
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
  } else {
    // 별도 정렬을 지정하지 않은 기본 상태 — 항상 등록일 오름차순(먼저 등록된 것부터)으로 정리한다.
    filtered = [...filtered].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }

  useEffect(() => { setPage(1); }, [activeCategory, activeManageItemId, activeMainMenuId, search, pageSize]);
  // 필터가 바뀌면(목록 내용이 달라지므로) 다중선택/정렬 상태도 함께 초기화한다.
  useEffect(() => { setSelectedCertIds(new Set()); setSortKey(null); }, [activeCategory, activeManageItemId, activeMainMenuId]);

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const pagedList  = pageSize === 'all' ? filtered : filtered.slice((page - 1) * pageSize, page * pageSize);

  // 목록 인쇄 — 다른 메뉴(공지사항 등)와 동일한 방식: 화면 캡처가 아니라 별도 창에 실제 표(<table>)를
  // 작성해서 인쇄한다. 현재 화면에 필터링(선택된 인증항목/관리항목/연월/검색어)되어 표시 중인 목록 그대로 인쇄된다.
  const handleListPrint = () => {
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(user); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)
    const esc = (s) => String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const rowsHtml = filtered.map(c => {
      const d = calcDday(c.expire_date);
      const ddayLabel = d === null ? '-' : (d < 0 ? `만료 ${Math.abs(d)}일 초과` : d === 0 ? '오늘 만료' : `D-${d}`);
      return `<tr>
        <td>${esc(c.cert_type)}</td>
        <td style="text-align:left;">${esc(c.cert_name)}</td>
        <td>${esc(c.cert_number) || '-'}</td>
        <td style="text-align:left;">${esc(c.org_name) || '-'}</td>
        <td>${esc(c.start_date) || '-'}</td>
        <td>${esc(c.expire_date) || '-'}</td>
        <td>${esc(ddayLabel)}</td>
        <td>${c.created_at ? esc(c.created_at.slice(0,10)) : '-'}</td>
      </tr>`;
    }).join('');

    const pw = window.open('', '_blank', 'width=1200,height=800');
    pw.document.write(`<!DOCTYPE html><html><head><title>인증관리</title>
      <style>
        @page { size: A4 landscape; margin: 0; }
        body { font-family: sans-serif; color:#222; padding:10mm; box-sizing:border-box; }
        h2 { font-size:18px; margin:0 0 10px; }
        table { width:100%; border-collapse:collapse; font-size:11px; }
        th, td { border:1px solid #ccc; padding:5px 6px; text-align:center; }
        th { background:#f4f4f4; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        .footer { display:flex; justify-content:space-between; margin-top:10px; padding-top:6px;
          border-top:1px solid #ddd; font-size:10px; color:#666; }
      </style></head><body>
      <h2>🏅 인증관리${activeCategory !== '전체' ? ` - ${esc(activeCategory)}` : ''}</h2>
      <table>
        <thead><tr>
          <th>인증항목</th><th>인증명</th><th>인증번호</th><th>인증기관</th><th>취득일</th><th>만료일</th><th>D-Day</th><th>등록일</th>
        </tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="8" style="color:#999;">표시할 인증이 없습니다.</td></tr>`}</tbody>
      </table>
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('인증관리', '인증관리 목록');
    setTimeout(() => { pw.print(); pw.close(); }, 400);
  };

  // ── 목록 다중선택 - 이동/삭제 핸들러 ────────────────────────
  const toggleSelectCert = (id) => {
    setSelectedCertIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedCertIds.has(c.id));
  const toggleSelectAll = () => {
    setSelectedCertIds(allFilteredSelected ? new Set() : new Set(filtered.map(c => c.id)));
  };
  const openBulkMoveModal = () => { setBulkMoveTarget(''); setShowBulkMoveModal(true); };
  const handleBulkMoveConfirm = () => {
    if (!bulkMoveTarget) { setMsg('이동할 관리항목을 선택해주세요.'); return; }
    setBulkActionLoading(true);
    API.post('/certs/bulk-move', { ids: Array.from(selectedCertIds), manage_item_id: Number(bulkMoveTarget) })
      .then(r => {
        setMsg(r.data?.message || '이동 완료!');
        setShowBulkMoveModal(false);
        setSelectedCertIds(new Set());
        load();
      })
      .catch(err => setMsg(err.response?.data?.message || '이동 실패'))
      .finally(() => setBulkActionLoading(false));
  };
  const handleBulkDelete = () => {
    if (!window.confirm(`선택한 ${selectedCertIds.size}건을 삭제하시겠습니까?\n첨부파일도 함께 삭제되며 되돌릴 수 없습니다.`)) return;
    setBulkActionLoading(true);
    API.delete('/certs/bulk', { data: { ids: Array.from(selectedCertIds) } })
      .then(r => {
        setMsg(r.data?.message || '삭제 완료!');
        setSelectedCertIds(new Set());
        load();
      })
      .catch(err => setMsg(err.response?.data?.message || '삭제 실패'))
      .finally(() => setBulkActionLoading(false));
  };

  // 트리구조 카드 목록에 파일을 드래그앤드롭하면, 별도 등록폼 없이 그 즉시 현재 선택된 관리항목(폴더)
  // 밑으로 파일명을 문서명 삼아 순차 등록한다(여러 파일을 한번에 놓으면 하나씩 순서대로 등록).
  const handleListDropUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    if (!activeCategory || activeCategory === '전체' || activeManageItemId === '전체') return;
    setDropUploading(true);
    let okCount = 0, failCount = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append('cert_type', activeCategory);
      fd.append('manage_item_id', activeManageItemId);
      const dotIdx = file.name.lastIndexOf('.');
      fd.append('cert_name', dotIdx > 0 ? file.name.slice(0, dotIdx) : file.name);
      fd.append('manage_dept', CERT_MANAGE_DEPT_DEFAULT);
      fd.append('file', file);
      try {
        await API.post('/certs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        okCount++;
      } catch (err) {
        failCount++;
      }
    }
    setDropUploading(false);
    setMsg(failCount === 0 ? `${okCount}건 업로드 완료!` : `${okCount}건 업로드 완료, ${failCount}건 실패`);
    load();
  };

  // 목록(표+페이지네이션) — 전체 폭으로 표시될 때와 트리구조 좌측 목록 옆에 표시될 때 모두 재사용한다.
  const renderListSection = () => (
    <>
      {filtered.length === 0 ? (
        // 업로드형 관리항목(트리구조 카드, 특정 노드 선택 상태)일 때는 빈 목록이어도 파일을
        // 드래그앤드롭하면 바로 등록할 수 있도록, 아래 채워진 목록과 동일한 드래그 핸들러를 붙인다.
        <div
          onDragEnter={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); setIsDragOverList(true); }) : undefined}
          onDragOver={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); }) : undefined}
          onDragLeave={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget === e.target) setIsDragOverList(false); }) : undefined}
          onDrop={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); setIsDragOverList(false); handleListDropUpload(e.dataTransfer.files); }) : undefined}
          style={{ textAlign:'center', padding:'60px 0', color:'#aaa', fontSize:14, position:'relative',
            borderRadius:12, background: isDragOverList ? '#EAF3FC' : 'transparent',
            border: isDragOverList ? '2px dashed #185FA5' : '2px dashed transparent', transition:'background 0.1s, border 0.1s' }}>
          {menuType === 'tree' && activeManageItemId !== '전체' && (isDragOverList || dropUploading) && (
            <div style={{ position:'absolute', inset:0, zIndex:5, display:'flex', alignItems:'center', justifyContent:'center',
              background:'rgba(230,241,251,0.9)', color:'#185FA5', fontSize:15, fontWeight:600, pointerEvents:'none', borderRadius:12 }}>
              {dropUploading ? '⏳ 업로드 중...' : '📥 여기에 파일을 놓아 바로 등록'}
            </div>
          )}
          등록된 인증이 없습니다.<br />
          {menuType === 'tree' && activeManageItemId !== '전체' ? (
            <span style={{ fontSize:13 }}>이 영역에 파일을 끌어다 놓으면 바로 등록됩니다.</span>
          ) : (
            canWriteCerts && <span style={{ fontSize:13 }}>상단 "+ 인증 등록" 버튼을 눌러 추가하세요.</span>
          )}
        </div>
      ) : (() => {
        // 트리구조 카드(menu_type='tree')는 어느 노드(양식관리 포함)든 통일된 6컬럼 목록을 쓰고,
        // 그 외(상단메뉴 카드)는 "전체" 선택시 기본 컬럼, 특정 카드가 선택돼 있고 그 항목에 별도 목록
        // 구성이 지정돼 있으면 그것을 사용한다.
        const columns = menuType === 'tree' ? TREE_NODE_LIST_COLUMNS : (CERT_LIST_LAYOUTS[activeCategory] || DEFAULT_CERT_LIST_COLUMNS);
        // 정부인증/지식재산권을 제외한 특정 인증항목(카드)을 보고 있고, 이동 또는 삭제 권한이 있으면
        // 맨 앞에 체크박스 열을 하나 추가해 다중선택 이동/삭제를 할 수 있게 한다.
        const showCheckbox = canBulkManage && (canWriteCerts || canDeleteCert);
        const gridCols = (showCheckbox ? '34px ' : '') + columns.map(col => col.width).join(' ');
        return (
        <div
          onDragEnter={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); setIsDragOverList(true); }) : undefined}
          onDragOver={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); }) : undefined}
          onDragLeave={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget === e.target) setIsDragOverList(false); }) : undefined}
          onDrop={menuType === 'tree' && activeManageItemId !== '전체' ? (e => { e.preventDefault(); e.stopPropagation(); setIsDragOverList(false); handleListDropUpload(e.dataTransfer.files); }) : undefined}
          style={{ background: isDragOverList ? '#EAF3FC' : '#fff', borderRadius:12,
            border: isDragOverList ? '2px dashed #185FA5' : '1px solid #e5e7eb', overflowX:'auto',
            boxShadow:'0 1px 4px rgba(0,0,0,0.06)', position:'relative', transition:'background 0.1s, border 0.1s' }}>
          {menuType === 'tree' && activeManageItemId !== '전체' && (isDragOverList || dropUploading) && (
            <div style={{ position:'absolute', inset:0, zIndex:5, display:'flex', alignItems:'center', justifyContent:'center',
              background:'rgba(230,241,251,0.9)', color:'#185FA5', fontSize:15, fontWeight:600, pointerEvents:'none' }}>
              {dropUploading ? '⏳ 업로드 중...' : '📥 여기에 파일을 놓아 바로 등록'}
            </div>
          )}
          {/* 다중선택 툴바 — 모두선택/해제 + 1건 이상 선택시 이동/삭제 버튼 활성화 */}
          {showCheckbox && (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 10px',
              background:'#f8fafc', borderBottom:'1px solid #e5e7eb', fontSize:13, color:'#666' }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', userSelect:'none' }}>
                <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} style={{ cursor:'pointer' }} />
                모두선택
              </label>
              <span>{selectedCertIds.size > 0 ? `${selectedCertIds.size}건 선택됨` : ''}</span>
              <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
                {canWriteCerts && (
                  <button onClick={openBulkMoveModal} disabled={selectedCertIds.size === 0 || bulkActionLoading}
                    style={{ padding:'5px 12px', border:'1px solid #185FA5', borderRadius:6, background:'#fff',
                      color: selectedCertIds.size === 0 ? '#ccc' : '#185FA5',
                      borderColor: selectedCertIds.size === 0 ? '#ddd' : '#185FA5',
                      fontSize:13, cursor: selectedCertIds.size === 0 ? 'not-allowed' : 'pointer' }}>
                    📦 선택 이동
                  </button>
                )}
                {canDeleteCert && (
                  <button onClick={handleBulkDelete} disabled={selectedCertIds.size === 0 || bulkActionLoading}
                    style={{ padding:'5px 12px', border:'1px solid #dc2626', borderRadius:6, background:'#fff',
                      color: selectedCertIds.size === 0 ? '#ccc' : '#dc2626',
                      borderColor: selectedCertIds.size === 0 ? '#ddd' : '#dc2626',
                      fontSize:13, cursor: selectedCertIds.size === 0 ? 'not-allowed' : 'pointer' }}>
                    🗑 선택 삭제
                  </button>
                )}
              </div>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns: gridCols,
            background:'#f8fafc', padding:'10px 10px', fontSize:14, fontWeight:600,
            color:'#555', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
            {showCheckbox && <span />}
            {columns.map(col => {
              // 목록 컬럼명을 클릭하면 해당 컬럼 기준 오름차순으로 정렬(다시 누르면 기본순으로 복귀) —
              // 트리구조 카드뿐 아니라 정부인증/지식재산권 등 그리드형 목록 전체 컬럼(다운로드 제외)에
              // 동일하게 적용한다. 별도의 정렬 방향 아이콘은 표시하지 않는다(항상 오름차순).
              const sortable = menuType === 'tree' ? TREE_SORTABLE_KEYS.has(col.key) : col.key !== 'download';
              const centered = CERT_LIST_CENTER_KEYS.has(col.key);
              return (
                <span key={col.key}
                  onClick={sortable ? () => toggleSort(col.key) : undefined}
                  style={{ textAlign: centered ? 'center' : 'left',
                    paddingLeft: centered ? 0 : '1em',
                    display:'inline-flex', alignItems:'center',
                    justifyContent: centered ? 'center' : 'flex-start', gap:4,
                    cursor: sortable ? 'pointer' : 'default', userSelect:'none',
                    color: sortKey === col.key ? '#185FA5' : '#555' }}>
                  {col.label}
                </span>
              );
            })}
          </div>
          {pagedList.map((c, i) => (
            <div key={c.id} onClick={() => setSelected(c)}
              style={{ display:'grid', gridTemplateColumns: gridCols,
                padding:'10px 10px', fontSize:14, textAlign:'center', alignItems:'center',
                borderBottom: i < pagedList.length - 1 ? '1px solid #f0f0f0' : 'none',
                cursor:'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              {showCheckbox && (
                <span onClick={e => e.stopPropagation()} style={{ textAlign:'center' }}>
                  <input type="checkbox" checked={selectedCertIds.has(c.id)} onChange={() => toggleSelectCert(c.id)} style={{ cursor:'pointer' }} />
                </span>
              )}
              {columns.map(col => {
                const truncate = CERT_LIST_TRUNCATE_KEYS.has(col.key);
                const centered = CERT_LIST_CENTER_KEYS.has(col.key);
                return (
                  <span key={col.key} style={{ textAlign: centered ? 'center' : 'left',
                    paddingLeft: centered ? 0 : '1em', color:'#666666', fontSize:14,
                    overflow: truncate ? 'hidden' : undefined, textOverflow: truncate ? 'ellipsis' : undefined,
                    whiteSpace: truncate ? 'nowrap' : undefined }}>
                    {renderCertListCell(col.key, c, { canDownload, onDownloadError: () => setMsg('다운로드 실패') })}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
        );
      })()}

      {/* 페이지네이션 (모두보기가 아닐 때만) */}
      {pageSize !== 'all' && totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:14 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === 1 ? '#ccc' : '#555', fontSize:14, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>◀</button>
          <span style={{ fontSize:14, color:'#666' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === totalPages ? '#ccc' : '#555', fontSize:14, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>▶</button>
        </div>
      )}
    </>
  );

  // 삭제
  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete(`/certs/${id}`);
    setSelected(null);
    setMsg('삭제 완료!');
    load();
  };

  // 수정 버튼 클릭
  const handleEdit = (cert) => {
    setEditData(cert);
    setShowForm(true);
    setSelected(null);
  };

  // 인증만료 - 갱신 없이 만료 처리 (첨부파일은 서버에서 자동으로 인증만료 폴더로 이동)
  const handleExpire = async (id) => {
    if (!window.confirm('인증만료 처리하시겠습니까?\n첨부파일이 "인증만료" 폴더로 이동됩니다.')) return;
    await API.patch(`/certs/${id}/expire`);
    setSelected(null);
    setMsg('인증만료 처리되었습니다.');
    load();
  };

  // 인증갱신 - 입력폼 열기 (기존 값으로 채워둠)
  const openRenewForm = (cert) => {
    setRenewData({
      cert_number: cert.cert_number || '',
      org_name: cert.org_name || '',
      start_date: cert.start_date || '',
      expire_date: cert.expire_date || '',
      note: cert.note || '',
    });
    setRenewFile(null);
    setShowRenewForm(true);
  };

  // 인증갱신 - 갱신정보 반영 (기존 첨부파일은 서버에서 자동으로 인증만료 폴더로 이동)
  const submitRenew = async () => {
    if (!selected) return;
    setRenewLoading(true);
    try {
      const fd = new FormData();
      Object.entries(renewData).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (renewFile) fd.append('file', renewFile);
      await API.post(`/certs/${selected.id}/renew`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowRenewForm(false);
      setSelected(null);
      setMsg('인증갱신 완료!');
      load();
    } catch (e) {
      setMsg('인증갱신 실패');
    } finally {
      setRenewLoading(false);
    }
  };

  // 특허등록 처리 - 버튼을 누른 날짜를 특허등록일로 기록하고, 연차만료일(scope)을 그 날짜+1년으로 자동 계산한다 (지식재산권 전용)
  const registerPatent = async () => {
    if (!selected) return;
    if (!window.confirm('오늘 날짜를 특허등록일로 기록하시겠습니까?\n연차만료일이 특허등록일+1년으로 자동 계산됩니다.')) return;
    setPatentRegLoading(true);
    try {
      const res = await API.patch(`/certs/${selected.id}/patent-register`);
      setSelected(prev => (prev ? { ...prev, expire_date: res.data.expire_date, scope: res.data.scope } : prev));
      setMsg('특허등록 처리 완료!');
      load();
    } catch (e) {
      setMsg('특허등록 처리 실패');
    } finally {
      setPatentRegLoading(false);
    }
  };

  // 연차료 납부 - 기존 연차만료일(scope)에서 선택한 년수만큼 연장한다 (지식재산권 전용)
  const payAnnuity = async (years) => {
    if (!selected) return;
    if (!window.confirm(`연차료 ${years}년치를 납부 처리하시겠습니까?\n연차만료일이 ${years}년 연장됩니다.`)) return;
    setAnnuityLoading(true);
    try {
      const res = await API.patch(`/certs/${selected.id}/annuity-pay`, { years });
      setSelected(prev => (prev ? { ...prev, scope: res.data.scope } : prev));
      setShowAnnuityPanel(false);
      setMsg(`연차료 ${years}년치 납부 처리 완료!`);
      load();
    } catch (e) {
      setMsg('연차료 납부 처리 실패');
    } finally {
      setAnnuityLoading(false);
    }
  };

  return (
    <div style={{ padding:24, maxWidth:1690 }}>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🏅 인증관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>보유 인증서를 등록하고 만료일을 관리합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={handleListPrint}
            style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          {canWriteCerts && (
            <>
              {canManageManageItem && (
                <button onClick={() => selectedCategoryObj && setShowManageItemModal(true)} disabled={!selectedCategoryObj}
                  title={selectedCategoryObj ? '' : '먼저 상단에서 인증항목을 선택하세요.'}
                  style={{ background: selectedCategoryObj ? '#64748B' : '#cbd5e1', color:'#fff', border:'none', borderRadius:8,
                    padding:'10px 18px', fontSize:14, fontWeight:600, cursor: selectedCategoryObj ? 'pointer' : 'not-allowed' }}>
                  🗂️ 관리항목
                </button>
              )}
              {canManageCategory && (
                <button onClick={() => setShowCategoryModal(true)}
                  style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                  📋 인증항목 등록
                </button>
              )}
              <button onClick={() => {
                  // 트리구조 카드에서 양식관리를 제외한 특정 관리항목(폴더)을 골라둔 상태면, 그 노드에
                  // 미리 지정된 item_type(업로드형/양식작성형)에 따라 바로 해당 등록화면을 연다.
                  // (예전에는 클릭할 때마다 매번 방식을 물었지만, 이제 노드 등록 시점에 한 번 정해두면 된다.
                  //  item_type이 없는 옛 데이터는 기존 동작과 동일하게 업로드형으로 취급한다.)
                  if (menuType === 'tree' && !isFormLibNode && activeManageItemId !== '전체') {
                    const activeNode = manageItems.find(m => m.id === activeManageItemId);
                    if (activeNode?.item_type === 'form') {
                      setShowDocComposer(true);
                    } else {
                      setShowForm(true); setEditData(null);
                    }
                  } else {
                    setShowForm(true); setEditData(null);
                  }
                }}
                style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                + 인증 등록
              </button>
            </>
          )}
        </div>
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 상단 카드메뉴 — 등록된 인증항목(KC/KCS/CE 등)을 카드로 표시. 클릭하면 아래 목록이 그
          항목으로 필터링된다(다시 누르면 전체보기로 복귀). 카드에는 인증명과 등록건수만 표시.
          카드 개수만큼 열을 균등분할(repeat(N,1fr))해서 항상 우측 끝까지 꽉 차도록 폭을 자동조절한다
          — 카드가 늘어나면 auto-fill처럼 줄바꿈되는 대신 각 카드 폭이 함께 줄어든다. */}
      {(() => {
        const cardList = [{ id:'all', name:'전체' }, ...categories];
        return (
          <div style={{ display:'grid', gridTemplateColumns: `repeat(${cardList.length}, 1fr)`, gap:12, marginBottom:20 }}>
            {cardList.map(cat => {
              const isAll = cat.id === 'all';
              // "전체" 카드의 등록건수는 실제 전체보기 목록에 노출되는 건수와 반드시 일치해야 하므로
              // 목록 필터(matchAllViewTreeFilter)와 동일한 기준(isVisibleInAllView)으로 집계한다.
              // 트리구조(menu_type='tree') 카드는 인증서(문서) 건수 대신, 그 카드의 최상위 트리(상단 탭으로
              // 표현되는 "메인 메뉴") 개수를 보여준다 — 하위 폴더까지 모두 세면 실제 탭 개수와 숫자가
              // 맞지 않으므로 parent_id가 없는(최상위) 관리항목만 센다.
              const count = isAll
                ? certs.filter(c => isVisibleInAllView(c, categories)).length
                : (cat.menu_type === 'tree'
                    ? allManageItems.filter(m => m.category_id === cat.id && !m.parent_id).length
                    : certs.filter(c => c.cert_type === cat.name).length);
              const active = activeCategory === cat.name;
              const style = isAll ? { color:'#475569', bg:'#E2E8F0' } : tabStyleOf(cat.name);
              return (
                <div key={cat.id} onClick={() => setActiveCategory(cat.name)}
                  style={{ background: active ? style.bg : '#fff', border: active ? `2px solid ${style.color}` : '1px solid #e5e7eb',
                    borderRadius:12, padding:'14px 16px', cursor:'pointer', boxShadow: active ? '0 2px 10px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
                    transition:'background 0.15s, box-shadow 0.15s' }}>
                  <div style={{ fontSize:14, fontWeight:600, color: active ? style.color : '#1a1a1a', marginBottom:8,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {cat.name}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:11, color: active ? style.color : '#999' }}>등록건수</span>
                    <span style={{ fontSize:20, fontWeight:700, color: style.color }}>{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
      {categories.length === 0 && (
        <div style={{ padding:'18px 0', textAlign:'center', color:'#aaa', fontSize:13,
          border:'1px dashed #e5e7eb', borderRadius:12, marginBottom:20 }}>
          등록된 인증항목이 없습니다. {canWriteCerts && '상단 "📋 인증항목 등록" 버튼을 눌러 KC, KCS, CE 등 인증항목을 먼저 등록하세요.'}
        </div>
      )}

      {/* 카드메뉴 아래 컨트롤 행: 좌측 = 관리항목 탭. 카드의 menu_type이 "상단메뉴"면 등록된 관리항목이
          그대로 탭이 되고, "트리구조"면 최상위 관리항목이 "메인 메뉴" 탭이 되며 그 하위 항목들은 목록
          위쪽 좌측에 디렉토리 트리로 별도 표시된다. "전체" 카드 선택시에는 탭 자체를 숨긴다.
          / 우측 = 검색 → 연월 선택 → 모두보기(표시개수) 순서 */}
      <div style={{ display:'flex', justifyContent: (selectedCategoryObj && (menuType === 'top' || menuType === 'tree')) ? 'space-between' : 'flex-end',
        alignItems:'center', marginBottom:16, paddingBottom:10, flexWrap:'wrap', gap:10, borderBottom:'1px solid #e5e7eb' }}>
        {/* 좌측: 관리항목 탭 (상단메뉴 방식일 때만) */}
        {selectedCategoryObj && menuType === 'top' && (
          <div style={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
            {[{ id:'전체', name:'전체' }, ...manageItems.filter(m => !m.parent_id)].map(item => {
              const count = item.id === '전체'
                ? certs.filter(c => c.cert_type === activeCategory).length
                : certs.filter(c => c.cert_type === activeCategory && c.manage_item_id === item.id).length;
              const active = activeManageItemId === item.id;
              return (
                <button key={item.id} onClick={() => setActiveManageItemId(item.id)}
                  style={{ padding:'8px 16px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                    borderBottom: active ? '2px solid #185FA5' : '2px solid #e5e7eb',
                    background:'none', color: active ? '#185FA5' : '#888' }}>
                  {item.name}
                  {count > 0 && (
                    <span style={{ fontSize:11, marginLeft:6, background: active ? '#185FA5' : '#e5e7eb',
                      color: active ? '#fff' : '#888', padding:'1px 7px', borderRadius:20 }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* 좌측: 메인 메뉴 탭 (트리구조 방식일 때만) — 최상위 관리항목을 탭으로 노출하고, 하나를 선택하면
            그 하위 항목들이 아래 좌측에 디렉토리 트리로 나타난다. "전체" 탭과 탭별 등록건수 배지는 노출하지 않는다. */}
        {selectedCategoryObj && menuType === 'tree' && (
          <div style={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
            {manageItems.filter(m => !m.parent_id).map(item => {
              const active = activeMainMenuId === item.id;
              return (
                <button key={item.id}
                  onClick={() => { setActiveMainMenuId(item.id); setActiveManageItemId('전체'); }}
                  style={{ padding:'8px 16px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                    borderBottom: active ? '2px solid #185FA5' : '2px solid #e5e7eb',
                    background:'none', color: active ? '#185FA5' : '#888' }}>
                  {item.name}
                </button>
              );
            })}
          </div>
        )}

        {/* 우측: 검색 → 연월 선택 → 모두보기(표시개수) */}
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          {/* 검색창 */}
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 인증명, 인증항목, 기관명 검색..."
            style={{ padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, width:220, boxSizing:'border-box' }} />

          {/* 페이지당 표시개수 */}
          <select value={pageSize} onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555',
              outline:'none', cursor:'pointer', background:'#fff' }}>
            <option value="10">10개씩</option>
            <option value="30">30개씩</option>
            <option value="50">50개씩</option>
            <option value="100">100개씩</option>
            <option value="all">모두보기</option>
          </select>
        </div>
      </div>

      {/* 목록 영역 — 선택된 카드가 "트리구조"이고 위에서 특정 메인 메뉴 탭을 선택한 경우에만 좌측에
          그 메인 메뉴의 하위 디렉토리 트리, 우측에 목록을 배치. 그 외(전체 선택 / 상단메뉴 방식 /
          트리구조지만 메인 메뉴 "전체" 선택시)에는 목록을 전체 폭으로 표시 */}
      {selectedCategoryObj && menuType === 'tree' && activeMainMenuId !== '전체' && !isFormLibNode ? (
        <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
          {/* 좌측: 선택된 메인 메뉴의 하위 디렉토리 트리 (스토리지 좌측 트리와 동일한 스타일, 다단계 확장/축소 지원) */}
          <div style={{ width:220, flexShrink:0, background:'#fff', border:'1px solid #e5e7eb', borderRadius:12,
            padding:'8px 0', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ padding:'6px 16px 8px', fontSize:11, color:'#aaa', fontWeight:600, borderBottom:'1px solid #f1f1f1', marginBottom:4 }}>
              📁 {activeMainMenuNode?.name || ''}
            </div>
            <div onClick={() => setActiveManageItemId('전체')}
              style={{ padding:'10px 16px', cursor:'pointer', fontSize:13, fontWeight: activeManageItemId === '전체' ? 600 : 500,
                color: activeManageItemId === '전체' ? '#185FA5' : '#555', background: activeManageItemId === '전체' ? '#E6F1FB' : 'transparent',
                borderLeft: activeManageItemId === '전체' ? '3px solid #185FA5' : '3px solid transparent',
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>전체</span>
              <span style={{ fontSize:11, color: activeManageItemId === '전체' ? '#185FA5' : '#999' }}>
                {certs.filter(c => c.cert_type === activeCategory && mainMenuSubtreeIds.includes(c.manage_item_id)).length}
              </span>
            </div>
            {(activeMainMenuNode?.children || []).map(node => (
              <ManageItemTreeNode key={node.id} node={node} depth={0}
                activeManageItemId={activeManageItemId} setActiveManageItemId={setActiveManageItemId}
                certs={certs} activeCategory={activeCategory}
                expandedNodes={expandedNodes} toggleExpandNode={toggleExpandNode} />
            ))}
            {(activeMainMenuNode?.children || []).length === 0 && (
              <div style={{ padding:'16px', textAlign:'center', color:'#aaa', fontSize:12 }}>
                등록된 하위 폴더가 없습니다.
              </div>
            )}
          </div>
          {/* 우측: 목록 */}
          <div style={{ flex:1, minWidth:0 }}>
            {renderListSection()}
          </div>
        </div>
      ) : renderListSection()}

      {/* 상세 모달 */}
      {selected && (() => {
        const style = tabStyleOf(selected.cert_type);
        const isIp = selected.cert_type === '지식재산권';
        // 양식관리(문서서식 라이브러리) 레코드는 인증만료/인증갱신/특허등록/연차등록 등 인증 갱신 워크플로우가
        // 필요없고, 상세 정보도 문서번호/비고/등록자/등록일 정도만 보여주면 충분하다.
        const isFormLibRecord = selected.cert_type === '산업안전인증(KCs)' && selected.manage_item === '양식관리';
        return (
          <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
            onClick={() => setSelected(null)}>
            <div style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div>
                  <span style={{ fontSize:12, padding:'3px 10px', borderRadius:20, fontWeight:500, background: style.bg, color: style.color }}>
                    {selected.cert_type}{selected.manage_item ? ` - ${selected.manage_item}` : ''}
                  </span>
                  {selected.status === '만료' && (
                    <span style={{ fontSize:12, padding:'3px 10px', borderRadius:20, fontWeight:600, background:'#fee2e2', color:'#dc2626', marginLeft:6 }}>
                      만료
                    </span>
                  )}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  {!isFormLibRecord && canWriteCerts && selected.status !== '만료' && (() => {
                    const expireRef = isIp ? selected.scope : selected.expire_date;
                    const isPastDue = calcDday(expireRef) !== null && calcDday(expireRef) < 0;
                    return (
                      <button onClick={() => handleExpire(selected.id)}
                        style={{ padding:'5px 10px', background: isPastDue ? '#fee2e2' : 'transparent', color:'#dc2626', border:'1px solid #dc2626', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:500, whiteSpace:'nowrap' }}>
                        인증만료
                      </button>
                    );
                  })()}
                  {/* 지식재산권이 아닌 일반 인증항목은 기존과 동일한 "인증갱신" 입력폼을 그대로 사용한다(양식관리 제외) */}
                  {!isIp && !isFormLibRecord && canWriteCerts && (
                    <button onClick={() => openRenewForm(selected)}
                      style={{ padding:'5px 10px', background:'#f0f6ff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:500, whiteSpace:'nowrap' }}>
                      인증갱신
                    </button>
                  )}
                  {/* 지식재산권 전용: 출원 등록 시점엔 특허등록일을 알 수 없으므로, 실제 등록된 날 이 버튼을
                      눌러 오늘 날짜를 특허등록일로 기록 + 연차만료일(등록일+1년)을 자동 계산한다.
                      특허등록일이 아직 없을 때만 노출되고, 기록되고 나면 사라지고 "연차등록" 버튼으로 넘어간다. */}
                  {isIp && canWriteCerts && selected.status !== '만료' && !selected.expire_date && (
                    <button onClick={registerPatent} disabled={patentRegLoading}
                      style={{ padding:'5px 10px', background:'#f0f6ff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:6, cursor: patentRegLoading ? 'not-allowed' : 'pointer', fontSize:12, fontWeight:500, whiteSpace:'nowrap' }}>
                      {patentRegLoading ? '처리중...' : '특허등록'}
                    </button>
                  )}
                  {/* 지식재산권 전용: 일반 "인증갱신" 대신 "연차등록"으로 대체 — 1/2/3년 선택에 따라 연차만료일이 연장된다.
                      특허등록일이 기록된 이후(연차만료일이 계산된 이후)에만 노출된다 */}
                  {isIp && canWriteCerts && selected.status !== '만료' && selected.expire_date && (
                    <button onClick={() => setShowAnnuityPanel(v => !v)}
                      style={{ padding:'5px 10px', background: showAnnuityPanel ? '#185FA5' : '#f0f6ff', color: showAnnuityPanel ? '#fff' : '#185FA5', border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:500, whiteSpace:'nowrap' }}>
                      연차등록
                    </button>
                  )}
                  <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
                </div>
              </div>

              {/* 제목은 별도 줄에서 모달 전체 폭을 사용 — 위 뱃지/버튼 행의 좁은 폭에 눌려 조기 줄바꿈되던 문제 수정 */}
              <h3 style={{ fontSize:18, fontWeight:600, margin:'0 0 20px', width:'100%' }}>{selected.cert_name}</h3>

              {/* 연차등록 - 1/2/3년 선택. 선택하면 기존 연차만료일에서 그만큼 연장된다 */}
              {isIp && showAnnuityPanel && (
                <div style={{ marginBottom:20, padding:14, background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:10 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#185FA5', marginBottom:6 }}>연차등록</div>
                  <div style={{ fontSize:12, color:'#888', marginBottom:10 }}>
                    현재 연차만료일: {selected.scope || '미입력'} — 선택한 년수만큼 연장됩니다.
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    {[1, 2, 3].map(years => (
                      <button key={years} onClick={() => payAnnuity(years)} disabled={annuityLoading}
                        style={{ flex:1, padding:'8px', background: annuityLoading ? '#9cc3e5' : '#185FA5', color:'#fff', border:'none', borderRadius:6, cursor: annuityLoading ? 'not-allowed' : 'pointer', fontSize:13, fontWeight:500 }}>
                        {annuityLoading ? '처리중...' : `${years}년`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 인증갱신 - 인라인 입력폼 */}
              {showRenewForm && (
                <div style={{ marginBottom:20, padding:14, background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:10 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#185FA5', marginBottom:10 }}>갱신정보 입력</div>
                  <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:3 }}>인증번호</label>
                      <input value={renewData.cert_number} onChange={e => setRenewData({ ...renewData, cert_number: e.target.value })}
                        style={{ width:'100%', padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                    </div>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:3 }}>인증기관</label>
                      <input value={renewData.org_name} onChange={e => setRenewData({ ...renewData, org_name: e.target.value })}
                        style={{ width:'100%', padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:3 }}>취득일</label>
                      <input type="date" value={renewData.start_date} onChange={e => setRenewData({ ...renewData, start_date: e.target.value })}
                        style={{ width:'100%', padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                    </div>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:3 }}>만료일</label>
                      <input type="date" value={renewData.expire_date} onChange={e => setRenewData({ ...renewData, expire_date: e.target.value })}
                        style={{ width:'100%', padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom:8 }}>
                    <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:3 }}>비고</label>
                    <input value={renewData.note} onChange={e => setRenewData({ ...renewData, note: e.target.value })}
                      style={{ width:'100%', padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                  </div>
                  <div style={{ marginBottom:12 }}>
                    <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:3 }}>첨부파일 (선택시 기존 파일은 인증만료 폴더로 이동)</label>
                    <FileDropZone fileName={renewFile?.name} onFile={setRenewFile} style={{ marginBottom:0 }} />
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={submitRenew} disabled={renewLoading}
                      style={{ flex:1, padding:'8px', background: renewLoading ? '#9cc3e5' : '#185FA5', color:'#fff', border:'none', borderRadius:6, cursor: renewLoading ? 'not-allowed' : 'pointer', fontSize:13, fontWeight:500 }}>
                      {renewLoading ? '처리중...' : '인증갱신'}
                    </button>
                    <button onClick={() => setShowRenewForm(false)} disabled={renewLoading}
                      style={{ flex:1, padding:'8px', background:'#f3f4f6', color:'#555', border:'1px solid #ddd', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:500 }}>
                      취소
                    </button>
                  </div>
                </div>
              )}

              {/* 상세 정보 (양식관리는 문서번호/비고/등록자/등록일만, 지식재산권은 출원번호/출원일/특허등록일/연차료 라벨 사용) */}
              {(isFormLibRecord ? [
                ['문서번호', selected.cert_number],
                ['비고', selected.note],
                ['등록자', selected.created_by],
                ['등록일', selected.created_at?.slice(0, 10)],
              ] : isIp ? [
                ['출원번호', selected.cert_number],
                ['인증 기관', selected.org_name],
                ['관리항목', selected.manage_item],
                ['출원일', selected.start_date],
                ['특허등록일', selected.expire_date],
                ['연차료', selected.scope],
                ['비고', selected.note],
                ['등록자', selected.created_by],
                ['등록일자', selected.created_at?.slice(0, 10)],
              ] : [
                ['인증 번호', selected.cert_number],
                ['인증 기관', selected.org_name],
                ['관리항목', selected.manage_item],
                ['취득일', selected.start_date],
                ['만료일', selected.expire_date],
                ['적용 범위', selected.scope],
                ['비고', selected.note],
                ['등록자', selected.created_by],
                ['등록일', selected.created_at?.slice(0, 10)],
              ]).map(([label, val]) => val ? (
                <div key={label} style={{ display:'flex', gap:12, marginBottom:10, fontSize:14 }}>
                  <span style={{ width:80, color:'#888', flexShrink:0 }}>{label}</span>
                  <span style={{ color:'#1a1a1a', fontWeight: (label === '만료일' || label === '연차료') ? 600 : 400 }}>
                    {val} {(label === '만료일' || label === '연차료') && <DdayBadge date={val} plusFormat={isIp} />}
                  </span>
                </div>
              ) : null)}

              {/* 파일 다운로드 (지식재산권은 출원번호통지서/출원서 2건 각각 표시) */}
              {isIp ? (
                <>
                  {selected.file_name && canDownload && (
                    <div style={{ marginTop:16, padding:'12px 14px', background:'#f0f6ff', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:12, color:'#185FA5', fontWeight:600, marginBottom:3 }}>📎 출원번호통지서</div>
                        <div style={{ fontSize:13, color:'#555', wordBreak:'break-all' }}>{selected.file_ori}</div>
                      </div>
                      <span onClick={() => downloadCertFile(selected.file_name, selected.file_ori, () => setMsg('다운로드 실패'))}
                        style={{ background:'#185FA5', color:'#fff', padding:'6px 14px', borderRadius:6, fontSize:13, whiteSpace:'nowrap', flexShrink:0, cursor:'pointer' }}>
                        다운로드
                      </span>
                    </div>
                  )}
                  {selected.file_name2 && canDownload && (
                    <div style={{ marginTop:8, padding:'12px 14px', background:'#f0f6ff', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:12, color:'#185FA5', fontWeight:600, marginBottom:3 }}>📎 출원서</div>
                        <div style={{ fontSize:13, color:'#555', wordBreak:'break-all' }}>{selected.file_ori2}</div>
                      </div>
                      <span onClick={() => downloadCertFile(selected.file_name2, selected.file_ori2, () => setMsg('다운로드 실패'))}
                        style={{ background:'#185FA5', color:'#fff', padding:'6px 14px', borderRadius:6, fontSize:13, whiteSpace:'nowrap', flexShrink:0, cursor:'pointer' }}>
                        다운로드
                      </span>
                    </div>
                  )}
                </>
              ) : (
                selected.file_name && canDownload && (
                  <div style={{ marginTop:16, padding:'12px 14px', background:'#f0f6ff', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:13, color:'#185FA5' }}>📎 {selected.file_ori}</span>
                    <span onClick={() => downloadCertFile(selected.file_name, selected.file_ori, () => setMsg('다운로드 실패'))}
                      style={{ background:'#185FA5', color:'#fff', padding:'6px 14px', borderRadius:6, fontSize:13, cursor:'pointer' }}>
                      다운로드
                    </span>
                  </div>
                )
              )}

              {/* 수정/삭제 버튼 (권한설정에서 부여된 계정에게도 각각 노출) — 인증항목별 쓰기권한(category_write_scope) 검사 */}
              {(canEditSelected || canDeleteSelected) && (
                <div style={{ display:'flex', gap:8, marginTop:20 }}>
                  {canEditSelected && (
                    <button onClick={() => handleEdit(selected)}
                      style={{ flex:1, padding:'10px', background:'#f0f6ff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
                      ✏️ 수정
                    </button>
                  )}
                  {canDeleteSelected && (
                    <button onClick={() => handleDelete(selected.id)}
                      style={{ flex:1, padding:'10px', background:'#fee2e2', color:'#dc2626', border:'1px solid #dc2626', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
                      🗑 삭제
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 등록/수정 모달 */}
      {showForm && (
        <CertForm
          editData={editData}
          categories={categories}
          defaultCertType={selectedCategoryObj?.name}
          defaultManageItemId={isFormLibNode ? activeMainMenuNode.id : (activeManageItemId !== '전체' ? activeManageItemId : undefined)}
          hasListItem={hasListItem}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={() => { setShowForm(false); setEditData(null); load(); setMsg(editData ? '수정 완료!' : '등록 완료!'); }}
        />
      )}

      {/* 인증등록 방식 선택 — 트리구조 카드에서 관리항목(폴더)을 선택한 상태로 "+ 인증 등록"을 눌렀을 때만 노출 */}
      {showComposerChoice && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setShowComposerChoice(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:26, width:380, maxWidth:'90vw', boxShadow:'0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:6 }}>인증 등록 방식 선택</div>
            <div style={{ fontSize:13, color:'#888', marginBottom:18 }}>어떤 방식으로 등록하시겠습니까?</div>
            <button onClick={() => { setShowComposerChoice(false); setShowForm(true); setEditData(null); }}
              style={{ width:'100%', textAlign:'left', padding:'14px 16px', marginBottom:10, border:'1px solid #ddd', borderRadius:10,
                background:'#fff', cursor:'pointer' }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#1a1a1a' }}>📎 파일 업로드로 등록</div>
              <div style={{ fontSize:12, color:'#999', marginTop:3 }}>이미 완성된 파일을 첨부해 바로 등록합니다.</div>
            </button>
            <button onClick={() => { setShowComposerChoice(false); setShowDocComposer(true); }}
              style={{ width:'100%', textAlign:'left', padding:'14px 16px', border:'1px solid #185FA5', borderRadius:10,
                background:'#E6F1FB', cursor:'pointer' }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#185FA5' }}>📝 양식 기반으로 작성</div>
              <div style={{ fontSize:12, color:'#185FA5', marginTop:3 }}>양식관리에 등록된 양식을 참고해 화면에서 바로 문서를 작성합니다.</div>
            </button>
            <button onClick={() => setShowComposerChoice(false)}
              style={{ width:'100%', marginTop:14, padding:'8px 0', border:'none', background:'none', color:'#999', fontSize:13, cursor:'pointer' }}>
              취소
            </button>
          </div>
        </div>
      )}

      {/* 양식 기반 문서 작성기 — 업무관리 회의록 작성과 유사한 형태의 화면 내 문서 작성기 */}
      {showDocComposer && (
        <DocComposerModal
          certs={certs}
          activeCategory={activeCategory}
          manageItemId={activeManageItemId}
          onClose={() => setShowDocComposer(false)}
          onSaved={() => { setShowDocComposer(false); load(); setMsg('등록 완료!'); }}
        />
      )}

      {/* 인증항목 등록 모달 — menu_type(상단메뉴/트리구조) 선택 포함 */}
      {showCategoryModal && (
        <TabListModal
          items={categories}
          apiBase="/certs/categories"
          title="📋 인증항목 등록"
          placeholder="예: KC, KCS, CE, ISO9001..."
          emptyText="등록된 인증항목이 없습니다."
          showMenuType
          presetOptions={CERT_CATEGORY_PRESETS}
          onClose={() => setShowCategoryModal(false)}
          onChanged={loadCategories}
        />
      )}

      {/* 관리항목 등록(관리) 모달 — 선택된 인증항목(카드) 1개에 종속.
          menu_type이 "트리구조"면 다단계 트리 전용 모달을, "상단메뉴"면 기존 평면 목록 모달을 사용한다. */}
      {showManageItemModal && selectedCategoryObj && menuType === 'tree' && (
        <ManageItemTreeModal
          category={selectedCategoryObj}
          items={manageItems}
          onClose={() => setShowManageItemModal(false)}
          onChanged={() => { loadManageItems(selectedCategoryObj.id); loadAllManageItems(); }}
        />
      )}
      {showManageItemModal && selectedCategoryObj && menuType !== 'tree' && (
        <TabListModal
          items={manageItems}
          apiBase="/certs/manage-items"
          title={`🗂️ ${selectedCategoryObj.name} - 관리항목 관리`}
          placeholder="예: 내부심사, 외부심사, 정기점검..."
          emptyText="등록된 관리항목이 없습니다."
          extraBody={{ category_id: selectedCategoryObj.id }}
          onClose={() => setShowManageItemModal(false)}
          onChanged={() => { loadManageItems(selectedCategoryObj.id); loadAllManageItems(); }}
        />
      )}

      {/* 다중선택 이동 모달 — 목록에서 체크박스로 고른 인증서들을 다른 관리항목으로 한번에 옮긴다
          (정부인증/지식재산권 제외, canBulkManage). 대상 목록은 현재 선택된 인증항목(카드)의
          관리항목 트리를 그대로 사용 — 트리구조 카드면 하위 노드까지 들여쓰기로 함께 보여준다. */}
      {showBulkMoveModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => !bulkActionLoading && setShowBulkMoveModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:24, width:380, boxShadow:'0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:14 }}>📦 선택 항목 이동 ({selectedCertIds.size}건)</div>
            <div style={{ fontSize:13, color:'#666', marginBottom:8 }}>이동할 관리항목을 선택하세요.</div>
            <select value={bulkMoveTarget} onChange={e => setBulkMoveTarget(e.target.value)}
              style={{ width:'100%', padding:'8px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:14, marginBottom:18 }}>
              <option value="">관리항목 선택...</option>
              {flattenManageTree(manageTree).map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button onClick={() => setShowBulkMoveModal(false)} disabled={bulkActionLoading}
                style={{ padding:'8px 16px', border:'1px solid #ddd', borderRadius:8, background:'#fff', color:'#555', fontSize:14, cursor:'pointer' }}>
                취소
              </button>
              <button onClick={handleBulkMoveConfirm} disabled={bulkActionLoading || !bulkMoveTarget}
                style={{ padding:'8px 16px', border:'none', borderRadius:8,
                  background: (!bulkMoveTarget || bulkActionLoading) ? '#9db8d8' : '#185FA5',
                  color:'#fff', fontSize:14, cursor: (!bulkMoveTarget || bulkActionLoading) ? 'not-allowed' : 'pointer' }}>
                {bulkActionLoading ? '이동 중...' : '이동'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 관리항목 트리 노드 (재귀) ──────────────────────────
// 트리구조(menu_type='tree') 카드의 좌측 패널에서, 자식이 있는 노드는 ▸/▾ 로 펼침/접힘 토글이 가능하다.
// 각 노드를 클릭하면 그 노드에 직접 등록된 인증만 필터링된다(하위 노드 인증까지 합산하지 않음).
function ManageItemTreeNode({ node, depth, activeManageItemId, setActiveManageItemId, certs, activeCategory, expandedNodes, toggleExpandNode }) {
  const isActive = activeManageItemId === node.id;
  const isOpen = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const count = certs.filter(c => c.cert_type === activeCategory && c.manage_item_id === node.id).length;

  // 이름을 클릭하면 그 항목을 선택하는 것과 동시에(하위 폴더가 있으면) 펼침/접힘도 함께 토글한다 —
  // 화살표(▸/▾)만 눌러야 펼쳐지던 것을, 스토리지 좌측 트리처럼 이름 클릭만으로도 펼치고 접을 수 있게 한다.
  const handleRowClick = () => {
    setActiveManageItemId(node.id);
    if (hasChildren) toggleExpandNode(node.id);
  };

  return (
    <>
      <div onClick={handleRowClick}
        style={{ padding:`8px 14px 8px ${14 + depth * 16}px`, cursor:'pointer', fontSize:13,
          fontWeight: isActive ? 600 : 500, color: isActive ? '#185FA5' : '#555',
          background: isActive ? '#E6F1FB' : 'transparent',
          borderLeft: isActive ? '3px solid #185FA5' : '3px solid transparent',
          display:'flex', justifyContent:'space-between', alignItems:'center', gap:6 }}>
        <span style={{ display:'flex', alignItems:'center', gap:4, overflow:'hidden' }}>
          {hasChildren ? (
            <span onClick={e => { e.stopPropagation(); toggleExpandNode(node.id); }}
              style={{ fontSize:10, color:'#999', width:12, flexShrink:0, cursor:'pointer' }}>
              {isOpen ? '▾' : '▸'}
            </span>
          ) : <span style={{ width:12, flexShrink:0 }} />}
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{node.name}</span>
        </span>
        {count > 0 && <span style={{ fontSize:11, color: isActive ? '#185FA5' : '#999', flexShrink:0 }}>{count}</span>}
      </div>
      {hasChildren && isOpen && node.children.map(child => (
        <ManageItemTreeNode key={child.id} node={child} depth={depth + 1}
          activeManageItemId={activeManageItemId} setActiveManageItemId={setActiveManageItemId}
          certs={certs} activeCategory={activeCategory}
          expandedNodes={expandedNodes} toggleExpandNode={toggleExpandNode} />
      ))}
    </>
  );
}

// ── 관리항목 트리 관리 모달 (menu_type='tree' 카드 전용) ──────────────────────────
// 최상위 항목 등록(상단 입력창) + 각 노드의 "+ 하위" 버튼으로 원하는 깊이까지 하위 항목을 추가할 수 있다.
// 순서변경(▲▼)은 같은 부모(형제) 사이에서만 동작한다.
//
// ※ TabListModal과 동일하게, 이 모달 안에서 하는 모든 등록/수정/삭제/순서변경은 화면(임시 draft)에만
//   반영되고 하단 "확인"을 눌러야 서버에 실제로 반영된다. "취소"(또는 ✕)를 누르면 아무 것도 반영하지 않고 닫힌다.
//   (단, draft 상태에서 하위 항목이 있는 노드를 삭제하면 화면에서는 하위 항목도 함께 사라진다 — 확인 시점에
//   서버에도 하위→상위 순서로 삭제가 반영된다.)
function ManageItemTreeModal({ category, items, onClose, onChanged }) {
  const apiBase = '/certs/manage-items';
  const [draftItems, setDraftItems] = useState(() => items.map(it => ({ ...it })));
  const [rootName, setRootName] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [addingParentId, setAddingParentId] = useState(null); // 하위추가 입력창이 열려있는 노드 id
  const [addingName, setAddingName] = useState('');
  const [addingType, setAddingType] = useState('upload'); // 하위추가 입력창의 업로드형/양식작성형 선택
  const [rootType, setRootType] = useState('upload'); // 최상위추가 입력창의 업로드형/양식작성형 선택
  const [expanded, setExpanded] = useState(new Set(items.filter(i => !i.parent_id).map(i => i.id)));
  const tempIdRef = useRef(-1);

  const tree = buildManageTree(draftItems);

  // 등록 — draft에만 추가(임시 id는 음수). 실제 등록은 확인 시점에 처리된다.
  // item_type: '업로드형'(upload, 기본값) | '양식작성형'(form) — 노드 하나당 한 가지 방식으로 고정된다.
  const addItem = (parentId, name, itemType) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const tempId = tempIdRef.current--;
    setDraftItems(prev => [...prev, { id: tempId, category_id: category.id, parent_id: parentId, name: trimmed, sort_order: 0, item_type: itemType === 'form' ? 'form' : 'upload' }]);
  };
  const handleAddRoot = () => { addItem(null, rootName, rootType); setRootName(''); setRootType('upload'); };
  const handleAddChild = (parentId) => {
    addItem(parentId, addingName, addingType);
    setAddingName('');
    setAddingType('upload');
    setAddingParentId(null);
    setExpanded(prev => new Set(prev).add(parentId));
  };
  // 등록된 노드의 업로드형/양식작성형을 바로 전환(draft만 변경 — 확인 시점에 서버로 반영)
  const setNodeType = (id, itemType) => {
    setDraftItems(prev => prev.map(it => it.id === id ? { ...it, item_type: itemType } : it));
  };

  // 하위 항목까지 함께 draft에서 제거(cascade) — 실제 삭제는 확인 시점에 하위→상위 순서로 처리된다.
  const collectDescendantIds = (id, list) => {
    const direct = list.filter(i => i.parent_id === id).map(i => i.id);
    return direct.concat(direct.flatMap(cid => collectDescendantIds(cid, list)));
  };
  const handleDelete = (id) => {
    const hasChildren = draftItems.some(i => i.parent_id === id);
    const confirmMsg = hasChildren
      ? '이 항목과 하위 항목이 모두 삭제됩니다. 계속하시겠습니까?'
      : '이 항목을 삭제하시겠습니까?';
    if (!window.confirm(confirmMsg)) return;
    setDraftItems(prev => {
      const toRemove = new Set([id, ...collectDescendantIds(id, prev)]);
      return prev.filter(it => !toRemove.has(it.id));
    });
  };

  const startEdit = (item) => { setEditingId(item.id); setEditingName(item.name); };
  const cancelEdit = () => { setEditingId(null); setEditingName(''); };
  const saveEdit = (id) => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    setDraftItems(prev => prev.map(it => it.id === id ? { ...it, name: trimmed } : it));
    cancelEdit();
  };

  // 같은 부모(형제) 사이에서만 순서변경 — draftItems 배열 내에서의 상대적 순서가 곧 표시순서이므로,
  // 두 형제 항목의 배열상 위치를 서로 바꿔주기만 하면 된다.
  const siblingsOf = (node) => draftItems.filter(i => (i.parent_id || null) === (node.parent_id || null));
  const handleMove = (node, delta) => {
    const siblings = siblingsOf(node);
    const index = siblings.findIndex(s => s.id === node.id);
    const target = index + delta;
    if (target < 0 || target >= siblings.length) return;
    const otherId = siblings[target].id;
    setDraftItems(prev => {
      const next = [...prev];
      const i1 = next.findIndex(it => it.id === node.id);
      const i2 = next.findIndex(it => it.id === otherId);
      [next[i1], next[i2]] = [next[i2], next[i1]];
      return next;
    });
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // 확인 — draft와 원본(items)을 비교해서 삭제(하위→상위 순)→이름변경→신규등록(상위→하위 순)→순서 순으로 서버에 반영
  const handleConfirm = async () => {
    setSaving(true);
    setMsg('');
    try {
      const draftIds = new Set(draftItems.map(it => it.id));

      // 1) 삭제된 항목 — 하위 항목이 먼저 삭제되도록 원본 기준 깊이가 깊은 것부터 처리
      const depthOf = (id) => {
        let depth = 0;
        let current = items.find(i => i.id === id);
        const seen = new Set();
        while (current && current.parent_id && !seen.has(current.id)) {
          seen.add(current.id);
          depth++;
          current = items.find(i => i.id === current.parent_id);
        }
        return depth;
      };
      const toDelete = items.filter(it => !draftIds.has(it.id)).sort((a, b) => depthOf(b.id) - depthOf(a.id));
      for (const it of toDelete) {
        await API.delete(`${apiBase}/${it.id}`);
      }

      // 2) 이름 또는 타입(업로드형/양식작성형)이 바뀐 기존 항목
      for (const it of draftItems) {
        if (it.id > 0) {
          const orig = items.find(o => o.id === it.id);
          if (orig && (orig.name !== it.name || orig.item_type !== it.item_type)) {
            await API.put(`${apiBase}/${it.id}`, { name: it.name, item_type: it.item_type });
          }
        }
      }

      // 3) 신규 등록 — 상위 노드부터(preorder) 처리해야 하위 노드의 parent_id(임시id)를 실제id로 바꿔 보낼 수 있다
      const idMap = {};
      const resolveParent = (pid) => (pid == null ? null : (pid > 0 ? pid : idMap[pid]));
      const draftTree = buildManageTree(draftItems);
      const addTreeNodes = async (nodes) => {
        for (const node of nodes) {
          if (node.id < 0) {
            const res = await API.post(apiBase, { name: node.name, category_id: category.id, parent_id: resolveParent(node.parent_id), item_type: node.item_type });
            idMap[node.id] = res.data.id;
          }
          if (node.children && node.children.length) {
            await addTreeNodes(node.children);
          }
        }
      };
      await addTreeNodes(draftTree);

      // 4) 최종 화면 순서를 부모 그룹별로 서버에 반영
      const groups = {};
      draftItems.forEach(it => {
        const realId = it.id > 0 ? it.id : idMap[it.id];
        const key = String(resolveParent(it.parent_id) ?? 'root');
        if (!groups[key]) groups[key] = [];
        groups[key].push(realId);
      });
      for (const key of Object.keys(groups)) {
        if (groups[key].length) await API.put(`${apiBase}/reorder`, { order: groups[key] });
      }

      onChanged();
      onClose();
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 취소 — draft를 버리고(서버에는 아무 것도 반영하지 않고) 그대로 닫는다.
  const handleCancel = () => { onClose(); };

  const renderNode = (node, depth) => {
    const isOpen = expanded.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const siblings = siblingsOf(node);
    const idxInSiblings = siblings.findIndex(s => s.id === node.id);

    return (
      <div key={node.id}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:6,
          padding:`8px 14px 8px ${14 + depth * 18}px`, borderBottom:'1px solid #f0f0f0' }}>
          <div style={{ display:'flex', flexDirection:'column' }}>
            <button onClick={() => handleMove(node, -1)} disabled={idxInSiblings === 0}
              style={{ background:'none', border:'none', fontSize:11, lineHeight:1, padding:'2px 4px',
                color: idxInSiblings === 0 ? '#ddd' : '#888', cursor: idxInSiblings === 0 ? 'not-allowed' : 'pointer' }}>▲</button>
            <button onClick={() => handleMove(node, 1)} disabled={idxInSiblings === siblings.length - 1}
              style={{ background:'none', border:'none', fontSize:11, lineHeight:1, padding:'2px 4px',
                color: idxInSiblings === siblings.length - 1 ? '#ddd' : '#888', cursor: idxInSiblings === siblings.length - 1 ? 'not-allowed' : 'pointer' }}>▼</button>
          </div>

          {hasChildren ? (
            <span onClick={() => toggleExpand(node.id)} style={{ fontSize:10, color:'#999', cursor:'pointer', width:12, flexShrink:0 }}>
              {isOpen ? '▾' : '▸'}
            </span>
          ) : <span style={{ width:12, flexShrink:0 }} />}

          {editingId === node.id ? (
            <input value={editingName} onChange={e => setEditingName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveEdit(node.id)}
              autoFocus
              style={{ flex:1, padding:'6px 10px', border:'1px solid #185FA5', borderRadius:6, fontSize:14, outline:'none' }} />
          ) : (
            <span style={{ flex:1, fontSize:14, color:'#1a1a1a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{node.name}</span>
          )}

          {editingId !== node.id && (
            <div style={{ display:'flex', border:'1px solid #ddd', borderRadius:6, overflow:'hidden', flexShrink:0 }}>
              <button onClick={() => setNodeType(node.id, 'upload')} title="업로드형 — 파일을 업로드하여 리스트로 표시"
                style={{ padding:'4px 8px', fontSize:11, border:'none', cursor:'pointer',
                  background: (node.item_type || 'upload') === 'upload' ? '#185FA5' : '#f8fafc',
                  color: (node.item_type || 'upload') === 'upload' ? '#fff' : '#888' }}>📎 업로드</button>
              <button onClick={() => setNodeType(node.id, 'form')} title="양식작성형 — 양식관리 서식을 가져와 작성 후 리스트로 표시"
                style={{ padding:'4px 8px', fontSize:11, border:'none', cursor:'pointer',
                  background: node.item_type === 'form' ? '#185FA5' : '#f8fafc',
                  color: node.item_type === 'form' ? '#fff' : '#888' }}>📝 양식작성</button>
            </div>
          )}

          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            {editingId === node.id ? (
              <>
                <button onClick={() => saveEdit(node.id)} style={{ background:'none', border:'none', color:'#185FA5', fontSize:12, cursor:'pointer' }}>저장</button>
                <button onClick={cancelEdit} style={{ background:'none', border:'none', color:'#888', fontSize:12, cursor:'pointer' }}>취소</button>
              </>
            ) : (
              <>
                <button onClick={() => { setAddingParentId(node.id); setAddingName(''); setExpanded(prev => new Set(prev).add(node.id)); }}
                  style={{ background:'none', border:'none', color:'#0F6E56', fontSize:12, cursor:'pointer' }}>+ 하위</button>
                <button onClick={() => startEdit(node)} style={{ background:'none', border:'none', color:'#185FA5', fontSize:12, cursor:'pointer' }}>수정</button>
                <button onClick={() => handleDelete(node.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer' }}>삭제</button>
              </>
            )}
          </div>
        </div>

        {addingParentId === node.id && (
          <div style={{ display:'flex', gap:6, padding:`8px 14px 8px ${14 + (depth + 1) * 18}px`, background:'#f8fafc' }}>
            <input value={addingName} onChange={e => setAddingName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddChild(node.id)}
              placeholder="하위 항목 이름" autoFocus
              style={{ flex:1, padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, outline:'none' }} />
            <div style={{ display:'flex', border:'1px solid #ddd', borderRadius:6, overflow:'hidden', flexShrink:0 }}>
              <button onClick={() => setAddingType('upload')}
                style={{ padding:'6px 8px', fontSize:11, border:'none', cursor:'pointer',
                  background: addingType === 'upload' ? '#185FA5' : '#fff', color: addingType === 'upload' ? '#fff' : '#888' }}>📎 업로드</button>
              <button onClick={() => setAddingType('form')}
                style={{ padding:'6px 8px', fontSize:11, border:'none', cursor:'pointer',
                  background: addingType === 'form' ? '#185FA5' : '#fff', color: addingType === 'form' ? '#fff' : '#888' }}>📝 양식작성</button>
            </div>
            <button onClick={() => handleAddChild(node.id)} disabled={!addingName.trim()}
              style={{ padding:'6px 12px', background: addingName.trim() ? '#0F6E56' : '#aaa', color:'#fff', border:'none', borderRadius:6, fontSize:12, cursor: addingName.trim() ? 'pointer' : 'not-allowed' }}>등록</button>
            <button onClick={() => { setAddingParentId(null); setAddingName(''); setAddingType('upload'); }}
              style={{ padding:'6px 10px', background:'#eee', color:'#555', border:'none', borderRadius:6, fontSize:12, cursor:'pointer' }}>취소</button>
          </div>
        )}

        {hasChildren && isOpen && node.children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={handleCancel}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:620, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🗂️ {category.name} - 관리항목 관리</h3>
          <button onClick={handleCancel} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <input value={rootName} onChange={e => setRootName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddRoot()}
            placeholder="최상위 항목 이름 (예: 품질관리)"
            style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' }} />
          <div style={{ display:'flex', border:'1px solid #ddd', borderRadius:8, overflow:'hidden', flexShrink:0 }}>
            <button onClick={() => setRootType('upload')}
              style={{ padding:'9px 10px', fontSize:12, border:'none', cursor:'pointer',
                background: rootType === 'upload' ? '#185FA5' : '#fff', color: rootType === 'upload' ? '#fff' : '#888' }}>📎 업로드</button>
            <button onClick={() => setRootType('form')}
              style={{ padding:'9px 10px', fontSize:12, border:'none', cursor:'pointer',
                background: rootType === 'form' ? '#185FA5' : '#fff', color: rootType === 'form' ? '#fff' : '#888' }}>📝 양식작성</button>
          </div>
          <button onClick={handleAddRoot} disabled={!rootName.trim()}
            style={{ padding:'9px 16px', background: !rootName.trim() ? '#aaa' : '#185FA5', color:'#fff',
              border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor: !rootName.trim() ? 'not-allowed' : 'pointer' }}>
            등록
          </button>
        </div>
        <div style={{ fontSize:11, color:'#999', marginBottom:8 }}>
          최상위 항목을 등록한 뒤, 각 항목의 "+ 하위" 버튼으로 원하는 깊이만큼 하위 단계를 추가할 수 있습니다. 각 항목의 📎업로드/📝양식작성 버튼으로 등록 방식을 선택할 수 있습니다.
        </div>
        <div style={{ fontSize:11, color:'#999', marginBottom:8 }}>
          등록/수정/삭제/순서변경은 하단 "확인"을 눌러야 실제로 반영됩니다.
        </div>
        {msg && <div style={{ fontSize:12, color:'#dc2626', marginBottom:10 }}>{msg}</div>}

        <div style={{ marginTop:12, border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
          {tree.length === 0 ? (
            <div style={{ padding:'24px 0', textAlign:'center', color:'#aaa', fontSize:13 }}>등록된 관리항목이 없습니다.</div>
          ) : (
            tree.map(node => renderNode(node, 0))
          )}
        </div>

        {/* 좌측: 확인 / 우측: 취소 — 두 버튼이 폭을 균등하게 나눠가져 전체 폭을 꽉 채운다.
            확인을 눌러야 위의 모든 변경사항이 실제로 반영된다 */}
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button onClick={handleConfirm} disabled={saving}
            style={{ flex:1, padding:'12px 0', background: saving ? '#aaa' : '#185FA5', color:'#fff', border:'none',
              borderRadius:8, fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '확인'}
          </button>
          <button onClick={handleCancel} disabled={saving}
            style={{ flex:1, padding:'12px 0', background:'#f1f5f9', color:'#555', border:'1px solid #ddd',
              borderRadius:8, fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 등록/수정 폼 컴포넌트 ──────────────────────────
// 인증항목(카드)별로 "인증 등록" 입력폼의 필드 구성/순서를 다르게 적용하기 위한 설정.
// 아직 개별적으로 정해지지 않은 인증항목은 DEFAULT_CERT_FORM_LAYOUT(기존 방식)을 그대로 사용하고,
// 사용자가 인증항목별로 폼을 하나씩 확정해줄 때마다 이 맵에 항목을 추가해나간다.
const DEFAULT_CERT_FORM_LAYOUT = {
  fileLabel: '첨부파일',
  order: ['cert_type', 'cert_name', ['cert_number', 'manage_dept'], 'org_name', 'manage_item', 'dates', 'scope', 'note', 'file'],
};
// order 항목은 문자열(단독 필드, 한 줄 전체를 차지) 또는 문자열 배열(그 배열에 속한 필드들이
// 한 줄에 나란히 표시됨)을 사용할 수 있다. 예: ['cert_type', 'manage_item']을 배열로 넣으면
// 인증항목/관리항목이 한 줄에 표시된다.
const CERT_FORM_LAYOUTS = {
  '정부인증': {
    fileLabel: '인증서 등록',
    order: [['cert_type', 'manage_item'], ['org_name', 'cert_name'], ['cert_number', 'manage_dept'], 'dates', 'note', 'file'],
  },
  // 지식재산권: 인증명→출원명, 인증번호→출원번호, 취득일→출원일로 라벨을 바꾼다.
  // 특허등록일/연차만료일(=scope, 다음 연차료 납부기한)은 등록 시점에는 아직 알 수 없는 값이라
  // 이 폼에는 넣지 않는다 — 대신 상세화면의 "특허등록" 버튼(누른 날짜를 특허등록일로 기록 +
  // 연차만료일을 등록일+1년으로 자동 계산)과 "연차료" 버튼(1/2/3년치 납부 시 연차만료일 연장)으로만 관리한다.
  // 첨부파일은 출원번호통지서/출원서 2개를 각각 등록한다(ip_files).
  '지식재산권': {
    labels: { cert_name: '출원명', cert_number: '출원번호', start_date: '출원일', expire_date: '특허등록일', scope: '연차만료일' },
    order: [['cert_type', 'manage_item'], ['org_name', 'cert_name'], ['cert_number', 'manage_dept'], 'start_date', 'note', 'ip_files'],
  },
};
// 산업안전인증(KCs) 카드의 "양식관리" 관리항목 전용 — 문서번호/문서명/첨부파일만 입력받는 간단한 폼.
// (인증기관/취득일/만료일/관리부서 등은 문서서식에는 필요없어 아예 order에서 뺐다. 관리항목도 항상
// "양식관리" 노드로 고정되므로 선택 UI를 노출하지 않는다 — Certs.js의 isFormLibNode/defaultManageItemId 참고.)
const FORM_LIB_FORM_LAYOUT = {
  labels: { cert_number: '문서번호', cert_name: '문서명' },
  fileLabel: '양식 파일 첨부',
  order: ['cert_type', ['cert_number', 'cert_name'], 'note', 'file'],
};
// 관리부서 선택지 — 목록에 없는 부서는 "기타 직접입력"으로 자유 텍스트를 입력한다.
const CERT_MANAGE_DEPT_PRESETS = ['경영기획', '기술연구소', '생산관리', '품질관리'];
const CERT_MANAGE_DEPT_DEFAULT = '경영기획';

// ── 양식별 문서 작성기 입력 레이아웃 정의 ──────────────────────────────
// 양식관리에 업로드된 서식(hwp)은 내용이 비어있는 파일이라 시스템이 필드 구조를 자동으로 알 수 없다.
// 그래서 cert_number(예: NSQ_0104_F01, routes/certs.js parseFormTemplateFileName 참고) 기준으로
// 문서명에 맞는 입력 필드를 여기에 직접 정의해둔다. 새 서식이 양식관리에 업로드되면 이 목록에
// cert_number를 key로 항목을 추가해서 그 서식에 맞는 입력 레이아웃을 지정해준다.
// shape:
//   'report'   - 문서정보(문서번호/작성일/작성자) 아래에 라벨이 있는 입력행이 이어지는 보고서형
//   'registry' - 문서정보 아래에 열(columns)로 구성된 표에 행을 추가하며 입력하는 대장/리스트형
// 아직 이 목록에 없는 서식을 선택하면 기존처럼 자유 형식(빈 입력창)으로 작성된다.
const CERT_TEMPLATE_LAYOUTS = {
  NSC_0101_F01: { title:'제품출하일지', shape:'registry', columns:['일자','품목명','수량','출하처','담당자','비고'] },
  NSP_0102_F01: { title:'수입검사 기준서', shape:'registry', columns:['검사항목','규격/기준','검사방법','판정기준','비고'] },
  NSP_0302_F01: { title:'완제품검사 기준서', shape:'registry', columns:['검사항목','규격/기준','검사방법','판정기준','비고'] },
  NSP_0601_F01: { title:'작업표준서', shape:'registry', columns:['공정순서','작업내용','관리기준','사용설비/치공구','안전유의사항'] },
  NSP_0801_F01: { title:'제조공정도', shape:'report', fields:[
    { key:'process_name', label:'공정명' },
    { key:'process_overview', label:'공정 개요', type:'textarea' },
  ]},
  NSQ_0104_F01: { title:'부적합품 보고서 관리대장', shape:'registry', columns:['번호','발생일','부적합 내용','조치사항','담당자','비고'] },
  NSQ_0104_F02: { title:'부적합품 보고서', shape:'report', fields:[
    { key:'occur_date', label:'발생일자', type:'date' },
    { key:'product', label:'해당 제품/공정' },
    { key:'defect_content', label:'부적합 내용', type:'textarea' },
    { key:'cause', label:'원인분석', type:'textarea' },
    { key:'action', label:'조치사항', type:'textarea' },
  ]},
  NSQ_0104_F03: { title:'시정 및 예방조치 요청서', shape:'report', fields:[
    { key:'request_date', label:'요청일자', type:'date' },
    { key:'target_dept', label:'대상 부서' },
    { key:'problem', label:'문제점', type:'textarea' },
    { key:'requirement', label:'요구사항', type:'textarea' },
    { key:'due_date', label:'조치기한', type:'date' },
  ]},
  NSQ_0104_F04: { title:'시정 및 예방조치 관리대장', shape:'registry', columns:['번호','요청일','문제점','조치내용','완료일','담당자'] },
  NSQ_0105_F01: { title:'교육훈련 계획서', shape:'report', fields:[
    { key:'purpose', label:'교육 목적', type:'textarea' },
    { key:'target', label:'교육 대상' },
    { key:'period', label:'교육 기간' },
    { key:'content', label:'교육 내용', type:'textarea' },
  ]},
  NSQ_0105_F02: { title:'사내외 교육일지', shape:'registry', columns:['일자','교육명','강사','참석인원','교육내용','비고'] },
  NSQ_0105_F03: { title:'자격인증 등록대장', shape:'registry', columns:['번호','성명','자격명','취득일','유효기간','비고'] },
  NSQ_0105_F04: { title:'자격인증 평가표', shape:'report', fields:[
    { key:'target', label:'평가 대상' },
    { key:'eval_date', label:'평가일자', type:'date' },
    { key:'eval_items', label:'평가 항목', type:'textarea' },
    { key:'eval_result', label:'평가 결과', type:'textarea' },
  ]},
  NSQ_0106_F01: { title:'방침관리 추진계획서', shape:'report', fields:[
    { key:'goal', label:'방침 목표', type:'textarea' },
    { key:'tasks', label:'추진 과제', type:'textarea' },
    { key:'schedule', label:'추진 일정' },
    { key:'dept', label:'담당 부서' },
  ]},
  NSQ_0107_F01: { title:'회의록', shape:'report', fields:[
    { key:'meeting_datetime', label:'회의일시', type:'date' },
    { key:'meeting_place', label:'회의장소' },
    { key:'attendees', label:'참석자' },
    { key:'content', label:'회의내용', type:'textarea' },
  ]},
  NSQ_0108_F01: { title:'지속적개선 계획서', shape:'report', fields:[
    { key:'goal', label:'개선 목표', type:'textarea' },
    { key:'plan', label:'추진 계획', type:'textarea' },
    { key:'schedule', label:'일정' },
    { key:'owner', label:'담당자' },
  ]},
  NSQ_0108_F02: { title:'지속적개선 활동 회의록', shape:'report', fields:[
    { key:'meeting_datetime', label:'회의일시', type:'date' },
    { key:'attendees', label:'참석자' },
    { key:'content', label:'개선활동 내용', type:'textarea' },
  ]},
  NSQ_0108_F03: { title:'지속적개선 결과보고서', shape:'report', fields:[
    { key:'task', label:'개선 과제' },
    { key:'result', label:'추진 결과', type:'textarea' },
    { key:'effect', label:'효과 분석', type:'textarea' },
  ]},
  NSQ_0202_F01: { title:'고객불만 관리대장', shape:'registry', columns:['번호','접수일','고객명','불만내용','처리결과','담당자'] },
  NSQ_0202_F02: { title:'고객불만 처리서', shape:'report', fields:[
    { key:'receive_date', label:'접수일', type:'date' },
    { key:'customer', label:'고객명' },
    { key:'complaint', label:'불만 내용', type:'textarea' },
    { key:'cause', label:'원인분석', type:'textarea' },
    { key:'result', label:'처리 결과', type:'textarea' },
  ]},
  NSQ_0203_F01: { title:'경영검토 보고서', shape:'report', fields:[
    { key:'review_date', label:'검토일자', type:'date' },
    { key:'attendees', label:'참석자' },
    { key:'agenda', label:'검토 안건', type:'textarea' },
    { key:'result', label:'검토 결과', type:'textarea' },
    { key:'followup', label:'후속 조치', type:'textarea' },
  ]},
  NSQ_0203_F02: { title:'내부품질감사 계획서', shape:'report', fields:[
    { key:'purpose', label:'감사 목적', type:'textarea' },
    { key:'scope', label:'감사 범위' },
    { key:'schedule', label:'감사 일정', type:'date' },
    { key:'team', label:'감사팀' },
  ]},
  NSQ_0203_F03: { title:'내부품질감사 종합보고서', shape:'report', fields:[
    { key:'audit_date', label:'감사일자', type:'date' },
    { key:'dept', label:'감사 부서' },
    { key:'findings', label:'지적 사항', type:'textarea' },
    { key:'opinion', label:'종합 의견', type:'textarea' },
  ]},
  NSQ_0203_F04: { title:'내부품질감사 체크리스트', shape:'registry', columns:['번호','점검항목','적합여부','지적사항','비고'] },
  NSQ_0203_F05: { title:'고객만족도 조사보고서', shape:'report', fields:[
    { key:'period', label:'조사 기간' },
    { key:'target', label:'조사 대상' },
    { key:'result', label:'조사 결과', type:'textarea' },
    { key:'improvement', label:'분석 및 개선사항', type:'textarea' },
  ]},
  NSQ_0203_F06: { title:'고객만족도 조사설문지', shape:'registry', columns:['번호','설문항목','응답(매우만족~불만족)','비고'] },
  NSQ_0401_F01: { title:'품질매뉴얼 표지', shape:'report', fields:[
    { key:'doc_title', label:'문서 제목' },
    { key:'revision_no', label:'개정 번호' },
    { key:'issue_date', label:'발행일', type:'date' },
  ]},
  NSQ_0401_F02: { title:'품질매뉴얼 내지', shape:'report', fields:[
    { key:'chapter', label:'장/절 제목' },
    { key:'content', label:'내용', type:'textarea' },
  ]},
  NSQ_0401_F03: { title:'품질절차 및 지침서 표지', shape:'report', fields:[
    { key:'procedure_name', label:'절차명' },
    { key:'revision_no', label:'개정 번호' },
    { key:'issue_date', label:'발행일', type:'date' },
  ]},
  NSQ_0401_F04: { title:'품질절차 및 지침서 내지', shape:'report', fields:[
    { key:'procedure_name', label:'절차명' },
    { key:'content', label:'절차 내용', type:'textarea' },
  ]},
  NSQ_0402_F01: { title:'파일관리대장', shape:'registry', columns:['번호','파일명','보관위치','담당자','비고'] },
  NSQ_0402_F02: { title:'양식관리대장', shape:'registry', columns:['번호','양식명','양식번호','제개정일','관리부서'] },
  NSQ_0402_F03: { title:'문서등록대장', shape:'registry', columns:['번호','문서명','문서번호','등록일','담당자'] },
  NSQ_0402_F04: { title:'문서접수대장', shape:'registry', columns:['번호','접수일','발신처','문서명','접수자'] },
  NSQ_0402_F05: { title:'문서배포대장', shape:'registry', columns:['번호','배포일','문서명','배포부서','수령자'] },
  NSQ_0402_F06: { title:'내용물목록표', shape:'registry', columns:['번호','품목명','수량','비고'] },
  NSQ_0501_F01: { title:'공급자 등록대장', shape:'registry', columns:['번호','업체명','대표자','연락처','등록일','비고'] },
  NSQ_0501_F02: { title:'공급자 평가서', shape:'report', fields:[
    { key:'company', label:'업체명' },
    { key:'eval_date', label:'평가일자', type:'date' },
    { key:'eval_items', label:'평가 항목', type:'textarea' },
    { key:'eval_result', label:'평가 결과', type:'textarea' },
  ]},
  NSQ_0501_F03: { title:'공급자 실태조사서', shape:'report', fields:[
    { key:'company', label:'업체명' },
    { key:'survey_date', label:'조사일자', type:'date' },
    { key:'content', label:'조사 내용', type:'textarea' },
    { key:'opinion', label:'종합 의견', type:'textarea' },
  ]},
  NSQ_0503_F01: { title:'재고현황 리스트', shape:'registry', columns:['번호','품목명','규격','수량','보관위치','비고'] },
  NSQ_0503_F02: { title:'제품 및 부품 식별표', shape:'report', fields:[
    { key:'item_name', label:'품목명' },
    { key:'spec', label:'규격' },
    { key:'id_no', label:'식별번호' },
    { key:'note', label:'비고' },
  ]},
  NSQ_0503_F03: { title:'보관점검 리스트', shape:'registry', columns:['번호','점검항목','점검결과','점검자','비고'] },
  NSQ_0601_F01: { title:'일일생산현황 보고서', shape:'report', fields:[
    { key:'prod_date', label:'생산일자', type:'date' },
    { key:'product', label:'생산 제품' },
    { key:'qty', label:'생산 수량' },
    { key:'note', label:'특이사항', type:'textarea' },
  ]},
  NSQ_0601_F02: { title:'생산현황 보고서_월간', shape:'report', fields:[
    { key:'month', label:'대상 월' },
    { key:'result', label:'생산 실적', type:'textarea' },
    { key:'note', label:'특이사항', type:'textarea' },
  ]},
  NSQ_0601_F03: { title:'생산현황 보고서_년간', shape:'report', fields:[
    { key:'year', label:'대상 연도' },
    { key:'result', label:'생산 실적', type:'textarea' },
    { key:'opinion', label:'종합 의견', type:'textarea' },
  ]},
  NSQ_0603_F01: { title:'데이터 분석결과 보고서', shape:'report', fields:[
    { key:'target', label:'분석 대상' },
    { key:'period', label:'분석 기간' },
    { key:'result', label:'분석 결과', type:'textarea' },
    { key:'improvement', label:'개선 방안', type:'textarea' },
  ]},
  NSQ_0604_F01: { title:'제조설비 정기점검 계획서', shape:'report', fields:[
    { key:'target_equipment', label:'점검 대상 설비' },
    { key:'cycle', label:'점검 주기' },
    { key:'plan', label:'점검 계획', type:'textarea' },
  ]},
  NSQ_0604_F02: { title:'제조설비 관리대장', shape:'registry', columns:['번호','설비명','규격','설치일','관리담당자','비고'] },
  NSQ_0604_F03: { title:'제조설비 이력카드', shape:'report', fields:[
    { key:'equipment', label:'설비명' },
    { key:'spec', label:'규격' },
    { key:'install_date', label:'설치일', type:'date' },
    { key:'history', label:'수리/점검 이력', type:'textarea' },
  ]},
  NSQ_0604_F04: { title:'제조설비 검수보고서', shape:'report', fields:[
    { key:'equipment', label:'설비명' },
    { key:'inspect_date', label:'검수일', type:'date' },
    { key:'result', label:'검수 결과', type:'textarea' },
  ]},
  NSQ_0604_F05: { title:'금형이력카드', shape:'report', fields:[
    { key:'mold_name', label:'금형명' },
    { key:'spec', label:'규격' },
    { key:'make_date', label:'제작일', type:'date' },
    { key:'history', label:'수리/점검 이력', type:'textarea' },
  ]},
  NSQ_0604_F06: { title:'치공구 지급 관리대장', shape:'registry', columns:['번호','지급일','치공구명','지급대상','반납일','비고'] },
  NSQ_0604_F07: { title:'제조설비 유지보수 의뢰서', shape:'report', fields:[
    { key:'equipment', label:'설비명' },
    { key:'fault', label:'고장 내용', type:'textarea' },
    { key:'request_date', label:'의뢰일', type:'date' },
    { key:'result', label:'처리 결과', type:'textarea' },
  ]},
  NSQ_0604_F08: { title:'제조설비 일상점검 종합표', shape:'registry', columns:['설비명','점검항목','점검결과','점검자','비고'] },
  NSQ_0604_F09: { title:'치공구설비 관리대장', shape:'registry', columns:['번호','치공구/설비명','규격','관리담당자','비고'] },
  NSQ_0604_F10: { title:'제조설비 일상점검 시트', shape:'registry', columns:['점검일','점검항목','점검결과','점검자'] },
  NSQ_0605_F01: { title:'5S 평가시트_사무실', shape:'registry', columns:['평가항목','평가결과','비고'] },
  NSQ_0605_F02: { title:'5S 평가시트_현장', shape:'registry', columns:['평가항목','평가결과','비고'] },
  NSQ_0605_F03: { title:'안전점검 일지', shape:'registry', columns:['점검일','점검구역','점검항목','점검결과','점검자'] },
  NSQ_0605_F04: { title:'보호구 지급대장', shape:'registry', columns:['번호','지급일','지급대상','보호구명','수량','비고'] },
  NSQ_0605_F05: { title:'소화기 점검대장', shape:'registry', columns:['번호','설치위치','점검일','점검결과','점검자'] },
  NSQ_0605_F06: { title:'MSDS 관리대장', shape:'registry', columns:['번호','화학물질명','제조사','비치위치','비고'] },
  NSQ_0606_F01: { title:'위험성 평가표', shape:'registry', columns:['위험요인','발생가능성','중대성','위험성','대책'] },
  NSQ_0606_F02: { title:'위험성 감소대책 실행 계획서', shape:'report', fields:[
    { key:'risk_factor', label:'대상 위험요인' },
    { key:'plan', label:'감소 대책', type:'textarea' },
    { key:'schedule', label:'추진 일정' },
    { key:'owner', label:'담당자' },
  ]},
  NSQ_0701_F01: { title:'자재품목 리스트', shape:'registry', columns:['번호','자재명','규격','공급처','비고'] },
  NSQ_0701_F02: { title:'수입검사 성적서', shape:'report', fields:[
    { key:'material', label:'자재명' },
    { key:'inspect_date', label:'검사일', type:'date' },
    { key:'result', label:'검사 결과', type:'textarea' },
    { key:'judgement', label:'판정' },
  ]},
  NSQ_0701_F03: { title:'입고관리 리스트', shape:'registry', columns:['번호','입고일','자재명','수량','공급처','비고'] },
  NSQ_0702_F01: { title:'측정장비 관리대장', shape:'registry', columns:['번호','장비명','규격','관리담당자','비고'] },
  NSQ_0702_F02: { title:'측정설비 점검기록표', shape:'registry', columns:['점검일','장비명','점검결과','점검자'] },
  NSQ_0702_F03: { title:'측정장비 이력카드', shape:'report', fields:[
    { key:'equipment', label:'장비명' },
    { key:'spec', label:'규격' },
    { key:'buy_date', label:'구매일', type:'date' },
    { key:'history', label:'수리/교정 이력', type:'textarea' },
  ]},
  NSQ_0702_F04: { title:'측정장비 검수보고서', shape:'report', fields:[
    { key:'equipment', label:'장비명' },
    { key:'inspect_date', label:'검수일', type:'date' },
    { key:'result', label:'검수 결과', type:'textarea' },
  ]},
  NSQ_0702_F05: { title:'측정장비 유지보수 의뢰서', shape:'report', fields:[
    { key:'equipment', label:'장비명' },
    { key:'fault', label:'고장 내용', type:'textarea' },
    { key:'request_date', label:'의뢰일', type:'date' },
    { key:'result', label:'처리 결과', type:'textarea' },
  ]},
  NSQ_0702_F06: { title:'검교정 계획서', shape:'report', fields:[
    { key:'target', label:'대상 장비' },
    { key:'cycle', label:'검교정 주기' },
    { key:'plan', label:'계획 내용', type:'textarea' },
  ]},
  NSQ_0703_F01: { title:'부적합품 관리대장', shape:'registry', columns:['번호','발생일','부적합내용','조치사항','담당자'] },
  NSQ_0703_F02: { title:'생산불량품 발생일지', shape:'registry', columns:['발생일','불량내용','수량','조치사항','담당자'] },
  NSQ_0710_F01: { title:'공정검사 성적서', shape:'report', fields:[
    { key:'process', label:'공정명' },
    { key:'inspect_date', label:'검사일', type:'date' },
    { key:'result', label:'검사 결과', type:'textarea' },
    { key:'judgement', label:'판정' },
  ]},
  NSQ_0710_F02: { title:'완제품검사 성적서', shape:'report', fields:[
    { key:'product', label:'제품명' },
    { key:'inspect_date', label:'검사일', type:'date' },
    { key:'result', label:'검사 결과', type:'textarea' },
    { key:'judgement', label:'판정' },
  ]},
  // 파일명이 "NS_KCS_..." 형태인 2개 서식(정부파일 비표 / 파일홀더 비표)은 파일명 파싱 규칙상
  // cert_number가 동일(NS_KCS)하게 생성되므로 한 항목으로 통합해서 지정한다.
  NS_KCS: { title:'정부파일 비표', shape:'report', fields:[
    { key:'file_name', label:'파일명' },
    { key:'storage_year', label:'보관연도' },
    { key:'dept', label:'관리부서' },
  ]},
};

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function nl2br(str) {
  return escapeHtml(str).replace(/\n/g, '<br/>');
}

// 선택한 양식(CERT_TEMPLATE_LAYOUTS)의 구성대로 문서형 HTML을 조립한다.
// 이 HTML이 그대로 certs.content_html 로 저장되고, 백엔드가 PDF로 변환해서 첨부파일로 사용한다
// (routes/certs.js generateComposedDocFile 참고 — 이 함수는 그대로 재사용).
function buildComposedDocumentHtml({ docTitle, certNumber, authorDept, authorPosName, todayStr, layout, fieldValues, tableRows, extraHtml }) {
  const headerRow = `
    <tr>
      <td style="padding:8px 10px;font-weight:700;font-size:12px;border:1px solid #000;text-align:center;width:80px;">문서번호</td>
      <td style="padding:8px 10px;font-size:13px;border:1px solid #000;width:170px;">${escapeHtml(certNumber) || '-'}</td>
      <td style="padding:8px 10px;font-weight:700;font-size:12px;border:1px solid #000;text-align:center;width:70px;">작성일</td>
      <td style="padding:8px 10px;font-size:13px;border:1px solid #000;width:100px;">${escapeHtml(todayStr)}</td>
      <td style="padding:8px 10px;font-weight:700;font-size:12px;border:1px solid #000;text-align:center;width:70px;">작성자</td>
      <td style="padding:8px 10px;font-size:13px;border:1px solid #000;">${escapeHtml(authorDept)} ${escapeHtml(authorPosName)}</td>
    </tr>`;

  let fieldRowsHtml = '';
  if (layout?.shape === 'report') {
    fieldRowsHtml = (layout.fields || []).map(f => `
      <tr>
        <td style="padding:8px 10px;font-weight:700;font-size:12px;border:1px solid #000;text-align:center;">${escapeHtml(f.label)}</td>
        <td style="padding:8px 10px;font-size:13px;border:1px solid #000;" colspan="5">${nl2br(fieldValues?.[f.key])}</td>
      </tr>`).join('');
  }

  let registryHtml = '';
  if (layout?.shape === 'registry') {
    const cols = layout.columns || [];
    registryHtml = `
      <div style="padding:7px 10px;font-weight:700;font-size:12px;border-top:1px solid #000;border-bottom:1px solid #000;text-align:center;">${escapeHtml(layout.title)}</div>
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr>${cols.map(c => `<th style="padding:6px 4px;font-size:11px;font-weight:700;border:1px solid #000;background:#f8fafc;">${escapeHtml(c)}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${(tableRows || []).map(row => `<tr>${cols.map(c => `<td style="padding:6px 8px;font-size:12px;border:1px solid #000;text-align:center;">${escapeHtml(row[c])}</td>`).join('')}</tr>`).join('')}
        </tbody>
      </table>`;
  }

  const extraSectionHtml = extraHtml ? `
    <div style="padding:7px 10px;font-weight:700;font-size:12px;border-top:1px solid #000;border-bottom:1px solid #000;text-align:center;">기타 내용</div>
    <div style="padding:14px 16px;font-size:13px;line-height:1.6;">${extraHtml}</div>` : '';

  return `
    <div style="border:2px solid #000;font-family:'Malgun Gothic',sans-serif;">
      <div style="padding:16px 0;text-align:center;font-size:22px;font-weight:800;letter-spacing:8px;background:#fff;border-bottom:2px solid #000;">
        ${escapeHtml(layout?.title || docTitle)}
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <tbody>
          ${headerRow}
          ${fieldRowsHtml}
        </tbody>
      </table>
      ${registryHtml}
      ${extraSectionHtml}
    </div>`;
}

// ── 양식 기반 문서 작성기 ────────────────────────────────────────────
// 업무관리(회의록 작성)와 같은 형태 — 양식관리에 등록된 양식(문서서식)을 선택하면 그 양식의 실제
// 모양(문서번호/작성일/작성자 정보란 + 서식별 필드)이 그대로 표시되면서 직접 입력할 수 있다.
// 저장하면 백엔드가 조립된 문서 HTML을 PDF로 변환해서 일반 파일 업로드 등록과 동일하게 목록/다운로드에
// 노출한다(routes/certs.js generateComposedDocFile 참고).
function DocComposerModal({ certs, activeCategory, manageItemId, onClose, onSaved }) {
  const templates = (certs || []).filter(c => c.cert_type === activeCategory && c.manage_item === '양식관리');
  const [selectedCertNumber, setSelectedCertNumber] = useState('');
  const [certNumber, setCertNumber] = useState('');
  const [certName, setCertName] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [tableRows, setTableRows] = useState([]);
  const contentRef = useRef(null);

  // ── 서식(xlsx) 기반 입력 — 양식관리에 실제 PDF→엑셀 변환 서식이 있는 경우, 그 서식의 실제 셀
  // 구조를 그대로 표로 그려서 그 위에 바로 입력하게 한다(회의록 작성화면과 동일한 방식이되, 서식마다
  // 직접 레이아웃을 정의할 필요 없이 서버가 서식 파일에서 그대로 뽑아준 그리드를 사용한다).
  // 이 서식이 있으면 아래 CERT_TEMPLATE_LAYOUTS(구, 수기 레이아웃) 방식보다 우선한다.
  const [xlsxSheets, setXlsxSheets] = useState(null); // null: 아직 로딩 전/해당없음, [] 또는 [sheet,...]
  const [xlsxAnswers, setXlsxAnswers] = useState({}); // { "sheetIdx-row-col": "값" }
  const [loadingGrid, setLoadingGrid] = useState(false);

  const layout = xlsxSheets ? null : (CERT_TEMPLATE_LAYOUTS[selectedCertNumber] || null);
  const selectedTemplate = templates.find(t => t.cert_number === selectedCertNumber);

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const authorDept = user.role === 'superadmin' ? '' : (user.department || '');
  const authorPosName = user.role === 'superadmin'
    ? '최고관리자'
    : (user.position ? `${user.position} ${user.name || ''}`.trim() : (user.name || ''));
  const todayStr = getKstDateString();

  const emptyRow = (columns) => (columns || []).reduce((acc, c) => { acc[c] = ''; return acc; }, {});

  const handleTemplateSelect = async (certNum) => {
    setSelectedCertNumber(certNum);
    const t = templates.find(x => x.cert_number === certNum);
    setXlsxSheets(null);
    setXlsxAnswers({});
    setFieldValues({});
    setTableRows([]);
    if (!certNum) return;

    // 1순위: 서식관리에 업로드된 원본 서식을 그대로 엑셀로 변환해둔 xlsx가 있으면 그 실제 셀 구조를 받아온다.
    setLoadingGrid(true);
    try {
      const { data } = await API.get(`/certs/form-template/${encodeURIComponent(certNum)}`);
      if (data?.hasXlsx && data.sheets?.length) {
        setXlsxSheets(data.sheets);
        if (t) {
          if (!certName) setCertName(t.cert_name);
          if (!certNumber) setCertNumber(t.cert_number);
        }
        setLoadingGrid(false);
        return;
      }
    } catch (e) { /* 무시하고 아래 구방식으로 대체 */ }
    setLoadingGrid(false);

    // 2순위(구방식): 손으로 정의해둔 레이아웃(CERT_TEMPLATE_LAYOUTS)이 있으면 그것을 사용, 없으면 자유 형식.
    const newLayout = CERT_TEMPLATE_LAYOUTS[certNum] || null;
    if (t) {
      if (!certName) setCertName(newLayout?.title || t.cert_name);
      if (!certNumber) setCertNumber(t.cert_number);
    }
    if (newLayout?.shape === 'report') {
      const initial = {};
      (newLayout.fields || []).forEach(f => { initial[f.key] = ''; });
      setFieldValues(initial);
      setTableRows([]);
    } else if (newLayout?.shape === 'registry') {
      setFieldValues({});
      setTableRows([emptyRow(newLayout.columns)]);
    } else {
      setFieldValues({});
      setTableRows([]);
    }
  };

  const updateXlsxAnswer = (sheetIdx, row, col, value) => {
    setXlsxAnswers(prev => ({ ...prev, [`${sheetIdx}-${row}-${col}`]: value }));
  };

  const addRow = () => setTableRows(rows => [...rows, emptyRow(layout?.columns)]);
  const removeRow = (idx) => setTableRows(rows => rows.filter((_, i) => i !== idx));
  const updateRowCell = (idx, col, value) => setTableRows(rows => rows.map((r, i) => i === idx ? { ...r, [col]: value } : r));
  const updateField = (key, value) => setFieldValues(v => ({ ...v, [key]: value }));

  const exec = (cmd, value) => {
    contentRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const handleSubmit = async () => {
    if (!certName.trim()) { alert('문서명을 입력해주세요.'); return; }

    // 서식(xlsx) 기반 입력 — 서식 그대로의 표에 입력한 값을 그 서식의 빈 칸에 채워서 PDF로 저장한다.
    if (xlsxSheets) {
      const hasAny = Object.values(xlsxAnswers).some(v => (v || '').toString().trim());
      if (!hasAny) { alert('내용을 한 곳 이상 입력해주세요.'); return; }
      setSaving(true);
      const fd = new FormData();
      fd.append('cert_type', activeCategory);
      if (manageItemId && manageItemId !== '전체') fd.append('manage_item_id', manageItemId);
      fd.append('cert_name', certName);
      fd.append('cert_number', certNumber);
      fd.append('manage_dept', CERT_MANAGE_DEPT_DEFAULT);
      fd.append('template_cert_number', selectedCertNumber);
      fd.append('form_answers', JSON.stringify(xlsxAnswers));
      try {
        await API.post('/certs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        onSaved();
      } catch (err) {
        alert(err.response?.data?.message || '오류가 발생했습니다.');
      } finally {
        setSaving(false);
      }
      return;
    }

    const noteHtml = contentRef.current?.innerHTML || '';
    const noteIsEmpty = !noteHtml.trim() || noteHtml === '<br>';

    if (layout?.shape === 'report') {
      const hasAny = Object.values(fieldValues).some(v => (v || '').toString().trim());
      if (!hasAny && noteIsEmpty) { alert('내용을 입력해주세요.'); return; }
    } else if (layout?.shape === 'registry') {
      const hasAny = tableRows.some(row => Object.values(row).some(v => (v || '').toString().trim()));
      if (!hasAny && noteIsEmpty) { alert('내용을 한 줄 이상 입력해주세요.'); return; }
    } else {
      if (noteIsEmpty) { alert('문서 내용을 작성해주세요.'); return; }
    }

    const html = buildComposedDocumentHtml({
      docTitle: certName,
      certNumber,
      authorDept,
      authorPosName,
      todayStr,
      layout,
      fieldValues,
      tableRows: layout?.shape === 'registry' ? tableRows.filter(row => Object.values(row).some(v => (v || '').toString().trim())) : [],
      extraHtml: noteIsEmpty ? '' : noteHtml,
    });

    setSaving(true);
    const fd = new FormData();
    fd.append('cert_type', activeCategory);
    if (manageItemId && manageItemId !== '전체') fd.append('manage_item_id', manageItemId);
    fd.append('cert_name', certName);
    fd.append('cert_number', certNumber);
    fd.append('manage_dept', CERT_MANAGE_DEPT_DEFAULT);
    fd.append('content_html', html);
    fd.append('template_ref', selectedTemplate?.cert_name || '');
    try {
      await API.post('/certs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };
  const labelStyle = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };
  const toolBtn = { padding:'5px 10px', border:'1px solid #ddd', borderRadius:6, background:'#fff', fontSize:13, cursor:'pointer' };
  const docInput = { width:'100%', padding:'6px 8px', border:'none', background:'transparent', fontSize:13, boxSizing:'border-box', outline:'none' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:820, maxWidth:'94vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>📝 양식 기반 문서 작성</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ marginBottom:12 }}>
          <label style={labelStyle}>양식 선택 (양식관리)</label>
          <select value={selectedCertNumber} onChange={e => handleTemplateSelect(e.target.value)} style={inputStyle}>
            <option value="">선택 안함 (자유 형식으로 작성)</option>
            {templates.map(t => <option key={t.id} value={t.cert_number}>{t.cert_name}</option>)}
          </select>
          {templates.length === 0 && (
            <div style={{ fontSize:12, color:'#aaa', marginTop:4 }}>양식관리에 등록된 양식이 없습니다. 참고 없이도 작성할 수 있습니다.</div>
          )}
          {loadingGrid && (
            <div style={{ fontSize:12, color:'#185FA5', marginTop:4 }}>서식을 불러오는 중...</div>
          )}
          {!loadingGrid && selectedCertNumber && !xlsxSheets && !layout && (
            <div style={{ fontSize:12, color:'#e08e0b', marginTop:4 }}>
              이 서식은 아직 전용 입력 레이아웃이 준비되지 않아 자유 형식으로 작성됩니다.
            </div>
          )}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div>
            <label style={labelStyle}>문서번호</label>
            <input value={certNumber} onChange={e => setCertNumber(e.target.value)} style={inputStyle} placeholder="예: QP-001" />
          </div>
          <div>
            <label style={labelStyle}>문서명 *</label>
            <input value={certName} onChange={e => setCertName(e.target.value)} style={inputStyle} placeholder="문서 제목" />
          </div>
        </div>

        {/* ── 서식(xlsx) 기반 입력 — 양식관리에 업로드된 서식 파일을 그대로 A4 엑셀로 변환해둔 실제
             셀 구조를 표로 그려서, 테두리는 있지만 원본 텍스트가 없는 칸(입력칸)에만 입력창을 올린다.
             저장하면 서버가 이 서식의 빈 칸에 값을 채워 PDF로 변환해서 첨부한다(내용 조립 없이
             서식 파일 자체를 그대로 사용하므로 100% 동일한 모양이 보장된다). ── */}
        {xlsxSheets && xlsxSheets.map((sheet, sIdx) => {
          const containerMaxWidth = 756;
          const totalWidth = (sheet.colWidths || []).reduce((a, b) => a + b, 0) || 1;
          const scale = totalWidth > containerMaxWidth ? containerMaxWidth / totalWidth : 1;
          const scaledColWidths = (sheet.colWidths || []).map(w => Math.max(20, Math.round(w * scale)));

          const rowsMap = {};
          (sheet.cells || []).forEach(c => {
            if (!rowsMap[c.row]) rowsMap[c.row] = [];
            rowsMap[c.row].push(c);
          });
          Object.values(rowsMap).forEach(arr => arr.sort((a, b) => a.col - b.col));

          return (
            <div key={sIdx} style={{ border:'2px solid #000', marginBottom:14, overflowX:'auto' }}>
              {xlsxSheets.length > 1 && (
                <div style={{ padding:'6px 10px', fontSize:11, fontWeight:700, color:'#666', background:'#f8fafc', borderBottom:'1px solid #ccc' }}>
                  {sIdx + 1}페이지 / {xlsxSheets.length}페이지{sheet.name ? ` — ${sheet.name}` : ''}
                </div>
              )}
              <table style={{ borderCollapse:'collapse', tableLayout:'fixed', width: scaledColWidths.reduce((a,b)=>a+b,0) }}>
                <colgroup>
                  {scaledColWidths.map((w, i) => <col key={i} style={{ width:w }} />)}
                </colgroup>
                <tbody>
                  {Array.from({ length: sheet.nRows }, (_, i) => i + 1).map(r => {
                    const rowCells = rowsMap[r] || [];
                    if (!rowCells.length) return null;
                    return (
                      <tr key={r}>
                        {rowCells.map(c => {
                          const isBig = c.isInput && (c.rowspan >= 3 || (c.colspan >= 3 && c.rowspan >= 2));
                          const key = `${sIdx}-${c.row}-${c.col}`;
                          const cellStyle = {
                            border: c.hasBorder ? '1px solid #000' : 'none',
                            padding: c.isInput ? 2 : '5px 6px',
                            fontSize: Math.max(10, Math.min(15, c.size || 11)),
                            fontWeight: c.bold ? 700 : 400,
                            textAlign: c.align || 'center',
                            verticalAlign: 'middle',
                            background: c.isInput ? '#fffef2' : 'transparent',
                          };
                          return (
                            <td key={c.col} rowSpan={c.rowspan} colSpan={c.colspan} style={cellStyle}>
                              {c.isInput ? (
                                isBig ? (
                                  <textarea value={xlsxAnswers[key] || ''} onChange={e => updateXlsxAnswer(sIdx, c.row, c.col, e.target.value)}
                                    style={{ ...docInput, minHeight:60, resize:'vertical', textAlign:c.align || 'left' }} />
                                ) : (
                                  <input value={xlsxAnswers[key] || ''} onChange={e => updateXlsxAnswer(sIdx, c.row, c.col, e.target.value)}
                                    style={{ ...docInput, textAlign:c.align || 'center' }} />
                                )
                              ) : (
                                nl2br(c.text) ? <span dangerouslySetInnerHTML={{ __html: nl2br(c.text) }} /> : null
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

        {/* ── 손으로 정의해둔 레이아웃(CERT_TEMPLATE_LAYOUTS) 방식 — 서식(xlsx)이 없는 서식 선택시에만 사용 ── */}
        {!xlsxSheets && (
        <div style={{ border:'2px solid #000', marginBottom:14 }}>
          <div style={{ padding:'14px 0', textAlign:'center', fontSize:20, fontWeight:800, letterSpacing:6, background:'#fff', borderBottom:'2px solid #000' }}>
            {layout?.title || certName || '문서'}
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding:'8px 10px', fontWeight:700, fontSize:12, border:'1px solid #000', textAlign:'center', width:80 }}>문서번호</td>
                <td style={{ padding:'8px 10px', fontSize:13, border:'1px solid #000', width:170 }}>{certNumber || '-'}</td>
                <td style={{ padding:'8px 10px', fontWeight:700, fontSize:12, border:'1px solid #000', textAlign:'center', width:70 }}>작성일</td>
                <td style={{ padding:'8px 10px', fontSize:13, border:'1px solid #000', width:100 }}>{todayStr}</td>
                <td style={{ padding:'8px 10px', fontWeight:700, fontSize:12, border:'1px solid #000', textAlign:'center', width:70 }}>작성자</td>
                <td style={{ padding:'8px 10px', fontSize:13, border:'1px solid #000' }}>{authorDept} {authorPosName}</td>
              </tr>

              {layout?.shape === 'report' && layout.fields.map(f => (
                <tr key={f.key}>
                  <td style={{ padding:'8px 10px', fontWeight:700, fontSize:12, border:'1px solid #000', textAlign:'center' }}>{f.label}</td>
                  <td style={{ padding:'4px 8px', border:'1px solid #000' }} colSpan={5}>
                    {f.type === 'textarea' ? (
                      <textarea value={fieldValues[f.key] || ''} onChange={e => updateField(f.key, e.target.value)}
                        style={{ ...docInput, minHeight:70, resize:'vertical' }} />
                    ) : f.type === 'date' ? (
                      <input type="date" value={fieldValues[f.key] || ''} onChange={e => updateField(f.key, e.target.value)}
                        style={docInput} />
                    ) : (
                      <input value={fieldValues[f.key] || ''} onChange={e => updateField(f.key, e.target.value)}
                        style={docInput} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {layout?.shape === 'registry' && (
            <>
              <div style={{ padding:'7px 10px', fontWeight:700, fontSize:12, borderTop:'1px solid #000', borderBottom:'1px solid #000', textAlign:'center' }}>
                {layout.title}
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {layout.columns.map(c => (
                      <th key={c} style={{ padding:'6px 4px', fontSize:11, fontWeight:700, border:'1px solid #000', background:'#f8fafc' }}>{c}</th>
                    ))}
                    <th style={{ padding:'6px 4px', fontSize:11, fontWeight:700, border:'1px solid #000', background:'#f8fafc', width:40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => (
                    <tr key={idx}>
                      {layout.columns.map(c => (
                        <td key={c} style={{ padding:'2px', border:'1px solid #000' }}>
                          <input value={row[c] || ''} onChange={e => updateRowCell(idx, c, e.target.value)}
                            style={{ ...docInput, textAlign:'center' }} />
                        </td>
                      ))}
                      <td style={{ padding:'2px', border:'1px solid #000', textAlign:'center' }}>
                        <button type="button" onClick={() => removeRow(idx)}
                          style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:14 }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding:'8px 10px' }}>
                <button type="button" onClick={addRow}
                  style={{ fontSize:12, padding:'5px 12px', background:'#EDE9FE', color:'#7C3AED', border:'1px solid #7C3AED', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                  + 행 추가
                </button>
              </div>
            </>
          )}
        </div>
        )}

        {!xlsxSheets && (
        <div style={{ marginBottom:8 }}>
          <label style={labelStyle}>{layout ? '기타 내용 (선택)' : '본문'}</label>
          <div style={{ display:'flex', gap:6, marginBottom:6 }}>
            <button type="button" onClick={() => exec('bold')} style={{ ...toolBtn, fontWeight:700 }}>B</button>
            <button type="button" onClick={() => exec('italic')} style={{ ...toolBtn, fontStyle:'italic' }}>I</button>
            <button type="button" onClick={() => exec('underline')} style={{ ...toolBtn, textDecoration:'underline' }}>U</button>
            <button type="button" onClick={() => exec('insertUnorderedList')} style={toolBtn}>• 목록</button>
            <button type="button" onClick={() => exec('insertOrderedList')} style={toolBtn}>1. 목록</button>
          </div>
          <div ref={contentRef} contentEditable suppressContentEditableWarning
            style={{ minHeight: layout ? 120 : 320, border:'1px solid #ddd', borderRadius:8, padding:14, fontSize:14, lineHeight:1.6, outline:'none', overflowY:'auto' }} />
        </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:18 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding:'9px 18px', border:'1px solid #ddd', borderRadius:8, background:'#fff', color:'#555', fontSize:14, cursor:'pointer' }}>
            취소
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding:'9px 18px', border:'none', borderRadius:8, background: saving ? '#9db8d8' : '#185FA5',
              color:'#fff', fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CertForm({ editData, categories, defaultCertType, defaultManageItemId, hasListItem, onClose, onSaved }) {
  const [form, setForm] = useState({
    cert_type: editData?.cert_type || defaultCertType || (categories[0]?.name || ''),
    cert_name: editData?.cert_name || '',
    cert_number: editData?.cert_number || '',
    org_name: editData?.org_name || '',
    manage_item_id: editData?.manage_item_id || defaultManageItemId || '',
    start_date: editData?.start_date || '',
    expire_date: editData?.expire_date || '',
    scope: editData?.scope || '',
    note: editData?.note || '',
    manage_dept: editData?.manage_dept || CERT_MANAGE_DEPT_DEFAULT,
  });
  const [file, setFile] = useState(null);
  const [file2, setFile2] = useState(null); // 두번째 첨부파일 (지식재산권 - 출원서 등 일부 인증항목만 사용)
  const [loading, setLoading] = useState(false);
  // 관리부서 드롭다운이 "기타 직접입력" 모드인지 여부 — 프리셋에 없는 값(수정 시 기존 값 포함)이면 자동으로 켜진다.
  const [deptCustom, setDeptCustom] = useState(() => !!editData?.manage_dept && !CERT_MANAGE_DEPT_PRESETS.includes(editData.manage_dept));
  const handleDeptSelect = e => {
    const value = e.target.value;
    if (value === '__custom__') {
      setDeptCustom(true);
      setForm(f => ({ ...f, manage_dept: CERT_MANAGE_DEPT_PRESETS.includes(f.manage_dept) ? '' : f.manage_dept }));
    } else {
      setDeptCustom(false);
      setForm(f => ({ ...f, manage_dept: value }));
    }
  };

  // 관리항목은 이제 인증항목(카드)에 종속되므로, 폼에서 선택한 cert_type이 바뀔 때마다 그 카드에
  // 등록된 관리항목만 새로 불러온다. cert_type이 바뀌면 이전에 선택돼 있던 관리항목은 초기화한다.
  const [formManageItems, setFormManageItems] = useState([]);
  useEffect(() => {
    const cat = categories.find(c => c.name === form.cert_type);
    if (cat) {
      API.get(`/certs/manage-items?category_id=${cat.id}`).then(r => setFormManageItems(r.data)).catch(() => setFormManageItems([]));
    } else {
      setFormManageItems([]);
    }
  }, [form.cert_type]); // eslint-disable-line

  const handleChange = e => {
    if (e.target.name === 'cert_type') {
      setForm({ ...form, cert_type: e.target.value, manage_item_id: '' });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  // 현재 선택된 인증항목(cert_type)에 대해 이 계정이 실제로 등록/수정 가능한지 — 등록된 인증항목별
  // 쓰기권한(category_write_scope) 검사. 백엔드가 최종적으로 막아주지만, 프론트에서도 미리 막아
  // 불필요한 요청/에러 메시지를 줄인다.
  const canSubmitType = !hasListItem || hasListItem('certs', 'category_write_scope', form.cert_type);

  const handleSubmit = async () => {
    if (!form.cert_type) return alert('인증항목을 선택해주세요.');
    if (!form.cert_name) return alert('인증명을 입력해주세요.');
    if (!canSubmitType) return alert('이 인증항목에 대한 등록/수정 권한이 없습니다. 최고관리자에게 문의해주세요.');
    setLoading(true);

    // 파일 포함이므로 FormData 사용
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append('file', file);
    if (file2) fd.append('file2', file2);

    try {
      if (editData) {
        await API.put(`/certs/${editData.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await API.post('/certs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const labelStyle = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'90vw', maxHeight:'85vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{editData ? '인증 수정' : '인증 등록'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {(() => {
          // 양식관리 노드로 등록/수정 중인지는 카드가 아니라 선택된 관리항목(노드) 이름으로 판단한다
          // (같은 산업안전인증(KCs) 카드 안에 인증관리/양식관리/일반관리/품질관리/생산관리가 함께 있으므로).
          const selectedManageItemNode = formManageItems.find(m => m.id === Number(form.manage_item_id));
          const isFormLib = form.cert_type === '산업안전인증(KCs)' && selectedManageItemNode?.name === '양식관리';
          const layout = isFormLib ? FORM_LIB_FORM_LAYOUT : (CERT_FORM_LAYOUTS[form.cert_type] || DEFAULT_CERT_FORM_LAYOUT);

          const fieldBlocks = {
            cert_type: (
              <div key="cert_type">
                <label style={labelStyle}>인증항목 *</label>
                {categories.length === 0 ? (
                  <div style={{ ...inputStyle, color:'#dc2626', background:'#fef2f2' }}>
                    등록된 인증항목이 없습니다. 먼저 상단 "📋 인증항목 등록"에서 KC, KCS, CE 등을 등록해주세요.
                  </div>
                ) : (
                  <select name="cert_type" value={form.cert_type} onChange={handleChange} style={inputStyle}>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                )}
              </div>
            ),
            manage_item: formManageItems.length > 0 ? (
              <div key="manage_item">
                <label style={labelStyle}>관리항목</label>
                <select name="manage_item_id" value={form.manage_item_id} onChange={handleChange} style={inputStyle}>
                  <option value="">선택 안함</option>
                  {flattenManageTree(buildManageTree(formManageItems)).map(opt => (
                    <option key={opt.id} value={opt.id} style={{ whiteSpace:'pre' }}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ) : null,
            cert_name: (
              <div key="cert_name">
                <label style={labelStyle}>{layout.labels?.cert_name || '인증명'} *</label>
                <input name="cert_name" placeholder="예: 품질경영시스템 인증" value={form.cert_name} onChange={handleChange} style={inputStyle} />
              </div>
            ),
            cert_number: (
              <div key="cert_number">
                <label style={labelStyle}>{layout.labels?.cert_number || '인증 번호'}</label>
                <input name="cert_number" placeholder="예: QMS-2024-001" value={form.cert_number} onChange={handleChange} style={inputStyle} />
              </div>
            ),
            manage_dept: (
              <div key="manage_dept">
                <label style={labelStyle}>관리부서</label>
                <select value={deptCustom ? '__custom__' : form.manage_dept} onChange={handleDeptSelect} style={inputStyle}>
                  {CERT_MANAGE_DEPT_PRESETS.map(d => <option key={d} value={d}>{d}</option>)}
                  <option value="__custom__">기타 직접입력</option>
                </select>
                {deptCustom && (
                  <input name="manage_dept" placeholder="관리부서 입력" value={form.manage_dept} onChange={handleChange}
                    style={{ ...inputStyle, marginTop:6 }} />
                )}
              </div>
            ),
            org_name: (
              <div key="org_name">
                <label style={labelStyle}>인증 기관</label>
                <input name="org_name" placeholder="예: 한국표준협회" value={form.org_name} onChange={handleChange} style={inputStyle} />
              </div>
            ),
            dates: (
              <div key="dates" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={labelStyle}>{layout.labels?.start_date || '취득일'}</label>
                  <input name="start_date" type="date" value={form.start_date} onChange={handleChange} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{layout.labels?.expire_date || '만료일'}</label>
                  <input name="expire_date" type="date" value={form.expire_date} onChange={handleChange} style={inputStyle} />
                </div>
              </div>
            ),
            // 지식재산권 전용: 출원일만 단독으로 입력 (특허등록일/연차만료일은 상세화면의
            // "특허등록"/"연차료" 버튼으로만 기록·계산되고, 등록 폼에서는 입력받지 않는다)
            start_date: (
              <div key="start_date">
                <label style={labelStyle}>{layout.labels?.start_date || '취득일'}</label>
                <input name="start_date" type="date" value={form.start_date} onChange={handleChange} style={inputStyle} />
              </div>
            ),
            scope: layout.scopeAsDate ? (
              <div key="scope">
                <label style={labelStyle}>{layout.labels?.scope || '적용 범위'}</label>
                <input name="scope" type="date" value={form.scope} onChange={handleChange} style={inputStyle} />
              </div>
            ) : (
              <div key="scope">
                <label style={labelStyle}>{layout.labels?.scope || '적용 범위'}</label>
                <input name="scope" placeholder="예: 금속 부품 설계 및 제조" value={form.scope} onChange={handleChange} style={inputStyle} />
              </div>
            ),
            note: (
              <div key="note">
                <label style={labelStyle}>비고</label>
                <textarea name="note" placeholder="특이사항 입력..." value={form.note} onChange={handleChange}
                  style={{ ...inputStyle, height:72, resize:'vertical' }} />
              </div>
            ),
            file: (
              <div key="file">
                <label style={labelStyle}>{layout.fileLabel}</label>
                <FileDropZone fileName={file?.name} currentLabel={editData?.file_ori} onFile={setFile} />
              </div>
            ),
            // 지식재산권 전용: 출원번호통지서 / 출원서 각각 별도로 첨부한다.
            ip_files: (
              <div key="ip_files" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={labelStyle}>출원번호통지서</label>
                  <FileDropZone fileName={file?.name} currentLabel={editData?.file_ori} onFile={setFile} />
                </div>
                <div>
                  <label style={labelStyle}>출원서</label>
                  <FileDropZone fileName={file2?.name} currentLabel={editData?.file_ori2} onFile={setFile2} />
                </div>
              </div>
            ),
          };

          return layout.order.map((key, idx) => {
            if (Array.isArray(key)) {
              const parts = key.map(k => fieldBlocks[k]).filter(Boolean);
              if (parts.length === 0) return null;
              return (
                <div key={`row-${idx}`} style={{ display:'grid', gridTemplateColumns:`repeat(${parts.length}, 1fr)`, gap:10 }}>
                  {parts}
                </div>
              );
            }
            return fieldBlocks[key];
          }).filter(Boolean);
        })()}

        <button onClick={handleSubmit} disabled={loading || categories.length === 0 || !canSubmitType}
          style={{ width:'100%', padding:'12px',
            background: (loading || categories.length === 0 || !canSubmitType) ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
            fontSize:15, fontWeight:600, cursor: (loading || categories.length === 0 || !canSubmitType) ? 'not-allowed' : 'pointer' }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '등록 완료')}
        </button>
      </div>
    </div>
  );
}

// ── 인증항목/관리항목 등록(관리) 공용 모달 컴포넌트 ──────────────────────────
// 등록/삭제뿐 아니라 순서변경(▲▼)과 이름수정(인라인)까지 지원한다(계약관리 목록등록과 동일한 패턴).
// apiBase(/certs/categories 또는 /certs/manage-items)만 바꿔주면 두 목록(인증항목/관리항목)에 동일하게 재사용된다.
// showMenuType: 인증항목(카드) 전용 — 상단메뉴/트리구조 선택 UI를 함께 표시한다.
// extraBody: 관리항목 전용 — 등록 시 함께 보낼 추가 필드(예: { category_id }).
//
// ※ 모든 등록/수정/삭제/순서변경은 이 모달 안에서는 화면(임시 draft)에만 반영되고,
//   하단 "확인" 버튼을 눌러야 실제로 서버에 반영된다. "취소"(또는 ✕)를 누르면 draft를 버리고
//   서버에는 아무 것도 반영하지 않은 채 닫힌다.
// 인증항목 등록 시 자주 쓰는 항목을 체크박스로 빠르게 등록할 수 있도록 제공하는 프리셋 목록
const CERT_CATEGORY_PRESETS = [
  '정부인증', '지식재산권', '한국산업표(KS인증)', '국가통합인증(KC인증)', 'K마크&Q마크',
  '산업안전인증(KCs)', '유럽 적합성(CE)', '미국 전기&전자제품 안전(UL)', '전자파 장애규제(FCC)',
  '품질경영시스템(ISO 9001)', '환경경영시스템(ISO 14001)', '안전보건경영시스템(ISO 45001)',
];

function TabListModal({ items, apiBase, title, placeholder, emptyText, showMenuType, extraBody, presetOptions, onClose, onChanged }) {
  const [draftItems, setDraftItems] = useState(() => items.map(it => ({ ...it })));
  const [name, setName] = useState('');
  const [menuType, setMenuType] = useState('top'); // showMenuType일 때만 사용
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [presetSelect, setPresetSelect] = useState('');
  const tempIdRef = useRef(-1);

  // 신규 등록 — draft에만 추가(임시 id는 음수). 실제 등록은 확인 시점에 처리된다.
  const handleAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const newItem = { id: tempIdRef.current--, name: trimmed, ...(showMenuType ? { menu_type: menuType } : {}) };
    setDraftItems(prev => [...prev, newItem]);
    setName('');
    if (showMenuType) setMenuType('top');
  };

  // 드롭다운에서 선택한 자주 쓰는 인증항목 1건을 draft에 추가 — draft에 이미 있는 이름이면 무시한다.
  const handleAddPreset = () => {
    const trimmed = presetSelect;
    if (!trimmed) return;
    if (draftItems.some(it => it.name === trimmed)) { setPresetSelect(''); return; }
    setDraftItems(prev => [...prev, { id: tempIdRef.current--, name: trimmed, ...(showMenuType ? { menu_type: 'top' } : {}) }]);
    setPresetSelect('');
  };

  // 삭제 — draft에서만 제거. 실제 삭제는 확인 시점에 처리된다.
  const handleDelete = (id) => {
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
    setDraftItems(prev => prev.filter(it => it.id !== id));
  };

  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };
  const cancelEdit = () => { setEditingId(null); setEditingName(''); };
  const saveEdit = (id) => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    setDraftItems(prev => prev.map(it => it.id === id ? { ...it, name: trimmed } : it));
    cancelEdit();
  };

  // 인증항목(카드)의 상단메뉴/트리구조 값 변경 — draft에만 반영
  const handleMenuTypeChange = (id, value) => {
    setDraftItems(prev => prev.map(it => it.id === id ? { ...it, menu_type: value } : it));
  };

  const handleMove = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= draftItems.length) return;
    setDraftItems(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // 확인 — draft와 원본(items)을 비교해서 삭제→이름/유형 변경→신규등록→순서 순으로 서버에 반영
  const handleConfirm = async () => {
    setSaving(true);
    setMsg('');
    try {
      const draftIds = new Set(draftItems.map(it => it.id));

      // 1) 삭제된 항목 (원본에는 있었지만 draft에는 없음)
      const toDelete = items.filter(it => !draftIds.has(it.id));
      for (const it of toDelete) {
        await API.delete(`${apiBase}/${it.id}`);
      }

      // 2) 이름/유형이 바뀐 기존 항목
      for (const it of draftItems) {
        if (it.id > 0) {
          const orig = items.find(o => o.id === it.id);
          if (orig && (orig.name !== it.name || (showMenuType && orig.menu_type !== it.menu_type))) {
            const body = { name: it.name };
            if (showMenuType) body.menu_type = it.menu_type;
            await API.put(`${apiBase}/${it.id}`, body);
          }
        }
      }

      // 3) 신규 등록 (임시 id는 음수) — 등록한 순서대로 처리하고, 실제 id를 매핑해둔다
      const idMap = {};
      const news = draftItems.filter(it => it.id < 0);
      for (const it of news) {
        const body = { name: it.name, ...(extraBody || {}) };
        if (showMenuType) body.menu_type = it.menu_type;
        const res = await API.post(apiBase, body);
        idMap[it.id] = res.data.id;
      }

      // 4) 최종 화면 순서를 그대로 서버에 반영
      const finalOrder = draftItems.map(it => (it.id > 0 ? it.id : idMap[it.id]));
      if (finalOrder.length) {
        await API.put(`${apiBase}/reorder`, { order: finalOrder });
      }

      onChanged();
      onClose();
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 취소 — draft를 버리고(서버에는 아무 것도 반영하지 않고) 그대로 닫는다.
  const handleCancel = () => { onClose(); };

  const inputStyle = { flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={handleCancel}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:440, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{title}</h3>
          <button onClick={handleCancel} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {presetOptions && presetOptions.length > 0 && (() => {
          const availablePresets = presetOptions.filter(p => !draftItems.some(it => it.name === p));
          return (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, color:'#555', fontWeight:600, marginBottom:6 }}>자주 쓰는 인증항목</div>
              {availablePresets.length === 0 ? (
                <div style={{ fontSize:12, color:'#aaa', marginBottom:8 }}>모든 기본 항목이 이미 등록되어 있습니다.</div>
              ) : (
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <select value={presetSelect} onChange={e => setPresetSelect(e.target.value)}
                    style={{ flex:1, padding:'9px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, color:'#333', background:'#fff', cursor:'pointer' }}>
                    <option value="">인증항목 선택...</option>
                    {availablePresets.map(presetName => (
                      <option key={presetName} value={presetName}>{presetName}</option>
                    ))}
                  </select>
                  <button onClick={handleAddPreset} disabled={!presetSelect}
                    style={{ padding:'9px 16px', background: !presetSelect ? '#aaa' : '#185FA5', color:'#fff',
                      border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor: !presetSelect ? 'not-allowed' : 'pointer' }}>
                    추가
                  </button>
                </div>
              )}
              <div style={{ fontSize:11, color:'#999', margin:'10px 0', borderTop:'1px dashed #e5e7eb', paddingTop:10 }}>
                목록에 없는 인증항목은 아래에 직접 입력해서 등록하세요.
              </div>
            </div>
          );
        })()}

        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <input value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder={placeholder} style={inputStyle} />
          {showMenuType && (
            <select value={menuType} onChange={e => setMenuType(e.target.value)}
              style={{ padding:'9px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, color:'#555', background:'#fff', cursor:'pointer' }}>
              <option value="top">상단메뉴</option>
              <option value="tree">트리구조</option>
            </select>
          )}
          <button onClick={handleAdd} disabled={!name.trim()}
            style={{ padding:'9px 16px', background: !name.trim() ? '#aaa' : '#185FA5', color:'#fff',
              border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor: !name.trim() ? 'not-allowed' : 'pointer' }}>
            등록
          </button>
        </div>
        {showMenuType && (
          <div style={{ fontSize:11, color:'#999', marginBottom:8 }}>
            선택한 인증항목의 하위 관리항목을 상단 탭으로 보여줄지, 좌측 트리로 보여줄지 정합니다.
          </div>
        )}
        <div style={{ fontSize:11, color:'#999', marginBottom:8 }}>
          등록/수정/삭제/순서변경은 하단 "확인"을 눌러야 실제로 반영됩니다.
        </div>
        {msg && <div style={{ fontSize:12, color:'#dc2626', marginBottom:10 }}>{msg}</div>}

        <div style={{ marginTop:12, border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
          {draftItems.length === 0 ? (
            <div style={{ padding:'24px 0', textAlign:'center', color:'#aaa', fontSize:13 }}>{emptyText}</div>
          ) : (
            draftItems.map((c, i) => (
              <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8,
                padding:'8px 14px', borderBottom: i < draftItems.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  <button onClick={() => handleMove(i, -1)} disabled={i === 0}
                    style={{ background:'none', border:'none', fontSize:11, lineHeight:1, padding:'2px 4px',
                      color: i === 0 ? '#ddd' : '#888', cursor: i === 0 ? 'not-allowed' : 'pointer' }}>▲</button>
                  <button onClick={() => handleMove(i, 1)} disabled={i === draftItems.length - 1}
                    style={{ background:'none', border:'none', fontSize:11, lineHeight:1, padding:'2px 4px',
                      color: i === draftItems.length - 1 ? '#ddd' : '#888', cursor: i === draftItems.length - 1 ? 'not-allowed' : 'pointer' }}>▼</button>
                </div>

                {editingId === c.id ? (
                  <input value={editingName} onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)}
                    autoFocus
                    style={{ flex:1, padding:'6px 10px', border:'1px solid #185FA5', borderRadius:6, fontSize:14, outline:'none' }} />
                ) : (
                  <span style={{ flex:1, fontSize:14, color:'#1a1a1a' }}>{c.name}</span>
                )}

                {showMenuType && editingId !== c.id && (
                  <select value={c.menu_type || 'top'} onChange={e => handleMenuTypeChange(c.id, e.target.value)}
                    style={{ fontSize:12, padding:'4px 6px', border:'1px solid #ddd', borderRadius:6, background:'#fff', color:'#555', cursor:'pointer', flexShrink:0 }}>
                    <option value="top">상단메뉴</option>
                    <option value="tree">트리구조</option>
                  </select>
                )}

                <div style={{ display:'flex', gap:10, flexShrink:0 }}>
                  {editingId === c.id ? (
                    <>
                      <button onClick={() => saveEdit(c.id)} style={{ background:'none', border:'none', color:'#185FA5', fontSize:13, cursor:'pointer' }}>저장</button>
                      <button onClick={cancelEdit} style={{ background:'none', border:'none', color:'#888', fontSize:13, cursor:'pointer' }}>취소</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(c)} style={{ background:'none', border:'none', color:'#185FA5', fontSize:13, cursor:'pointer' }}>수정</button>
                      <button onClick={() => handleDelete(c.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:13, cursor:'pointer' }}>삭제</button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* 좌측: 확인 / 우측: 취소 — 두 버튼이 폭을 균등하게 나눠가져 전체 폭을 꽉 채운다.
            확인을 눌러야 위의 모든 변경사항이 실제로 반영된다 */}
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button onClick={handleConfirm} disabled={saving}
            style={{ flex:1, padding:'12px 0', background: saving ? '#aaa' : '#185FA5', color:'#fff', border:'none',
              borderRadius:8, fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '확인'}
          </button>
          <button onClick={handleCancel} disabled={saving}
            style={{ flex:1, padding:'12px 0', background:'#f1f5f9', color:'#555', border:'1px solid #ddd',
              borderRadius:8, fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}
